import React, { useState, useRef, useEffect } from "react";
import Swal from "sweetalert2";
import API from "../../../api/api";
import { resolveAssetUrl } from "../../../api/config";
import { downloadHtmlAsPdf, printHtmlViaIframe } from "../../../utils/pdfDownloadHelper";
import {
  FaIdCard,
  FaDownload,
  FaPrint,
  FaTimes,
  FaRunning,
  FaUserShield,
  FaUserTie,
  FaMapMarkerAlt,
  FaPhoneAlt,
  FaCalendarAlt,
  FaSignature,
  FaUpload,
  FaTrash,
  FaCheckCircle,
  FaEdit,
  FaUsers,
  FaSearch,
} from "react-icons/fa";

const DEFAULT_AVATAR = "/images/default-user.png";

export const getCardPhoto = (photoPath) => {
  if (!photoPath || typeof photoPath !== "string" || !photoPath.trim()) {
    return DEFAULT_AVATAR;
  }
  return resolveAssetUrl(photoPath, { defaultPath: DEFAULT_AVATAR });
};

/**
 * Resolve participant district cleanly
 */
export const resolveParticipantDistrict = (item) => {
  if (!item) return "-";
  const raw =
    item.district ||
    item.player?.district ||
    item.district_name ||
    (item.districtRelation?.name ? item.districtRelation.name : "");
  if (!raw) return "-";
  if (typeof raw === "string") {
    const trimmed = raw.trim();
    return trimmed || "-";
  }
  if (typeof raw === "object" && raw.name) return raw.name;
  return String(raw);
};

/**
 * Resolve participant mobile cleanly
 */
export const resolveParticipantMobile = (item) => {
  if (!item) return "-";
  const raw =
    item.parent_mobile ||
    item.mobile ||
    item.player?.mobile ||
    item.player?.parent_mobile ||
    item.phone ||
    "";
  if (!raw || raw === "null" || raw === "undefined") return "-";
  const str = String(raw).trim();
  if (!str || str === "-") return "-";
  return str;
};

/**
 * Resolve participant team name cleanly
 */
export const resolveParticipantTeam = (item) => {
  if (!item) return "—";
  const raw =
    item.team_name ||
    item.team ||
    item.assigned_coach?.team_name ||
    item.active_mapping?.team_name ||
    (Array.isArray(item.mappings) && item.mappings.find((m) => m.team_name)?.team_name) ||
    (Array.isArray(item.assigned_coaches) && item.assigned_coaches.find((c) => c.team_name)?.team_name) ||
    "";
  if (!raw || raw === "null" || raw === "undefined") return "—";
  const str = String(raw).trim();
  return str || "—";
};

/**
 * Resolve participant coach name cleanly
 */
export const resolveParticipantCoach = (item) => {
  if (!item) return "—";
  if (Array.isArray(item.assigned_coaches) && item.assigned_coaches.length > 0) {
    const names = item.assigned_coaches
      .map((c) => (typeof c === "object" ? c.name : c))
      .filter(Boolean)
      .join(", ");
    if (names) return names;
  }
  const raw =
    item.assigned_coach?.name ||
    item.coach?.name ||
    item.coach_name ||
    item.active_mapping?.coach?.name ||
    (Array.isArray(item.mappings) && item.mappings.find((m) => m.coach?.name)?.coach?.name) ||
    "";
  if (!raw || raw === "null" || raw === "undefined") return "—";
  const str = String(raw).trim();
  return str || "—";
};

/**
 * Resolve participant category / classification cleanly
 */
export const resolveParticipantCategory = (item) => {
  if (!item) return "—";
  const raw =
    item.category ||
    item.player?.category ||
    (Array.isArray(item.player?.disability_types) ? item.player.disability_types[0] : "") ||
    (typeof item.player?.disability_types === "string" ? item.player.disability_types : "") ||
    "";
  if (!raw || raw === "null" || raw === "undefined") return "—";
  const str = String(raw).trim();
  return str || "—";
};

export const resolveParticipantDob = (item) => {
  const raw = item?.dob || item?.date_of_birth || item?.player?.dob || item?.player?.date_of_birth || "";
  if (!raw) return "—";
  const date = new Date(raw);
  return Number.isNaN(date.getTime()) ? String(raw).trim() || "—" : date.toLocaleDateString("en-IN");
};

export const resolveParticipantGender = (item) => {
  const raw = item?.gender || item?.player?.gender || "";
  return String(raw).trim() || "—";
};

/**
 * Cleanly format camp dates avoiding raw '2026 to 2026'
 */
export const formatCampDates = (camp) => {
  if (!camp) return "60 Days Residential Camp";
  const start = camp.start_date;
  const end = camp.end_date;
  if (!start || !end) {
    return camp.name || "60 Days Residential Camp";
  }
  try {
    const sDate = new Date(start);
    const eDate = new Date(end);
    if (!isNaN(sDate.getTime()) && !isNaN(eDate.getTime())) {
      const sStr = sDate.toLocaleDateString("en-IN", {
        day: "numeric",
        month: "short",
        year: "numeric",
      });
      const eStr = eDate.toLocaleDateString("en-IN", {
        day: "numeric",
        month: "short",
        year: "numeric",
      });
      return `${sStr} to ${eStr}`;
    }
  } catch (_e) { }
  return `${start} to ${end}`;
};

/**
 * Resolve camp venue, district, and dates cleanly
 */
export const resolveCampVenueAndDistrict = (camp, participantCard = null) => {
  const venue = camp?.venue || camp?.location || "State Sports Complex";
  const district =
    camp?.district ||
    (participantCard ? resolveParticipantDistrict(participantCard) : "") ||
    "Andhra Pradesh";
  return {
    venue: String(venue).trim(),
    district: String(district).trim(),
    display: `${String(venue).trim()} (${String(district).trim()})`,
  };
};

/**
 * Automatically trims empty whitespace/transparency around an uploaded signature
 * so it fits tightly edge-to-edge into any card signature slot without distortion.
 */
export function autoCropSignature(dataUrl) {
  if (!dataUrl || typeof dataUrl !== "string" || !dataUrl.startsWith("data:image")) {
    return Promise.resolve(dataUrl);
  }
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext("2d", { willReadFrequently: true });
        ctx.drawImage(img, 0, 0);

        const imgData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const d = imgData.data;

        let minX = canvas.width, minY = canvas.height, maxX = 0, maxY = 0;
        let found = false;

        for (let y = 0; y < canvas.height; y++) {
          for (let x = 0; x < canvas.width; x++) {
            const i = (y * canvas.width + x) * 4;
            const r = d[i];
            const g = d[i + 1];
            const b = d[i + 2];
            const a = d[i + 3];

            // Detect non-white and non-transparent ink pixels
            if (a > 25 && !(r > 238 && g > 238 && b > 238)) {
              found = true;
              if (x < minX) minX = x;
              if (x > maxX) maxX = x;
              if (y < minY) minY = y;
              if (y > maxY) maxY = y;
            }
          }
        }

        if (!found || minX >= maxX || minY >= maxY) {
          resolve(dataUrl);
          return;
        }

        // Add 2px breathing room around the trimmed ink
        const pad = 2;
        minX = Math.max(0, minX - pad);
        minY = Math.max(0, minY - pad);
        maxX = Math.min(canvas.width, maxX + pad);
        maxY = Math.min(canvas.height, maxY + pad);

        const w = maxX - minX;
        const h = maxY - minY;

        // Downscale to max 380px width, 120px height to keep base64 payload tiny (~15KB)
        // High-res uncompressed signatures inflate batch HTML to 100+ MB and freeze Chrome!
        const maxW = 380;
        const maxH = 120;
        let targetW = w;
        let targetH = h;
        if (targetW > maxW || targetH > maxH) {
          const ratio = Math.min(maxW / targetW, maxH / targetH);
          targetW = Math.max(1, Math.round(targetW * ratio));
          targetH = Math.max(1, Math.round(targetH * ratio));
        }

        const outCanvas = document.createElement("canvas");
        outCanvas.width = targetW;
        outCanvas.height = targetH;
        const outCtx = outCanvas.getContext("2d");
        outCtx.imageSmoothingEnabled = true;
        outCtx.imageSmoothingQuality = "high";
        outCtx.drawImage(canvas, minX, minY, w, h, 0, 0, targetW, targetH);

        resolve(outCanvas.toDataURL("image/png"));
      } catch (_e) {
        resolve(dataUrl);
      }
    };
    img.onerror = () => resolve(dataUrl);
    img.src = dataUrl;
  });
}

/**
 * Helper to resolve both President and Secretary signatures
 */
export function resolveCardSignatures(currentSignature, fallbackPresidentSig = "") {
  let secSig = "";
  let presSig = "";

  if (typeof currentSignature === "object" && currentSignature !== null) {
    secSig = currentSignature.secretary || "";
    presSig = currentSignature.president || "";
  } else if (typeof currentSignature === "string") {
    secSig = currentSignature;
  }

  if (fallbackPresidentSig && typeof fallbackPresidentSig === "string") {
    presSig = fallbackPresidentSig;
  }

  if (!secSig && typeof window !== "undefined") {
    try {
      secSig = localStorage.getItem("camp_secretary_signature") || "";
    } catch (_e) { }
  }
  if (!presSig && typeof window !== "undefined") {
    try {
      presSig = localStorage.getItem("camp_president_signature") || "";
    } catch (_e) { }
  }

  return { secretary: secSig, president: presSig };
}

/**
 * Render single ID card standalone HTML string for rock-solid PDF export & iframe printing
 */
export function renderIdCardHtml(card, camp, currentSignature, showTeamAndCoach, origin = "") {
  const isAthlete = card._cardType === "athlete";
  const isCoach = card._cardType === "coach";
  const isOfficial = card._cardType === "official";

  const name = card.name || "Participant Name";
  const fatherName =
    card.father_name ||
    card.relation ||
    (isOfficial || isCoach ? "" : "Guardian");
  const district = resolveParticipantDistrict(card);
  const mobile = resolveParticipantMobile(card);
  const teamName = resolveParticipantTeam(card);
  const coachName = resolveParticipantCoach(card);
  const dob = resolveParticipantDob(card);
  const gender = resolveParticipantGender(card);

  let photoUrl = getCardPhoto(
    card.photo ||
    card.player?.photo ||
    card.player_photo ||
    card.profile_photo ||
    card.user?.photo
  );
  if (photoUrl && photoUrl.startsWith("/") && origin) {
    photoUrl = origin + photoUrl;
  }
  const parasportsLogo = (origin ? origin : "") + "/images/parasports.png";
  const ssaLogo = (origin ? origin : "") + "/images/ssa.png";

  let roleTitle = "CAMP ATHLETE";
  let roleGradient = "linear-gradient(to right, #1e40af, #312e81, #0f172a)";
  let roleBorderColor = "#4f46e5";
  let roleBadgeBg = "#4f46e5";
  let roleTag = "ATHLETE";
  let idLabel = "ID NUMBER";
  let idVal = card.id_number && String(card.id_number).trim() !== "" ? String(card.id_number).trim() : "—";
  let secondaryLabel = "TEAM";
  let secondaryVal = teamName || "—";
  let athleteSport = "Para Sports";
  let athleteCategory = "—";

  if (isAthlete) {
    const rawDisc =
      card?.discipline_name ||
      card?.discipline?.name ||
      card?.player?.discipline_name ||
      "";
    if (!rawDisc || !rawDisc.trim()) {
      athleteSport = "Para Sports";
    } else if (/athletics/i.test(rawDisc)) {
      athleteSport = "Athletics";
    } else {
      athleteSport = rawDisc.trim();
    }
    athleteCategory = resolveParticipantCategory(card);
  } else if (isCoach) {
    roleTitle = "CAMP ASSISTANT COACH";
    roleGradient = "linear-gradient(to right, #b45309, #9a3412, #451a03)";
    roleBorderColor = "#d97706";
    roleBadgeBg = "#d97706";
    roleTag = "ASSISTANT COACH";
    idLabel = "COACH ID";
    idVal = card.coach_id || `CCH-${String(card.id || 1).padStart(3, "0")}`;
    secondaryLabel = "ASSIGNMENT";
    secondaryVal = card.team_name || "Coaching Staff";
  } else if (isOfficial) {
    roleTitle = card.designation || "CAMP OFFICIAL";
    roleGradient = "linear-gradient(to right, #065f46, #134e4a, #022c22)";
    roleBorderColor = "#059669";
    roleBadgeBg = "#059669";
    roleTag = card.duty_role || "OFFICIAL";
    idLabel = "OFFICIAL ID";
    idVal = card.official_id_card_no || `OFF-${String(card.id || 1).padStart(3, "0")}`;
    secondaryLabel = card.room_no ? "ROOM NO" : "DUTY ROLE";
    secondaryVal = card.room_no ? `Room ${card.room_no}` : (card.duty_role || "Official");
  }

  const nameLength = (name || "").length;
  let nameFontSize = "12px";
  if (nameLength > 30) nameFontSize = "10px";
  else if (nameLength > 20) nameFontSize = "11px";

  const campDates = formatCampDates(camp);
  const campInfo = resolveCampVenueAndDistrict(camp, card);
  const { secretary: activeSecSig, president: activePresSig } = resolveCardSignatures(currentSignature);

  return `
    <div class="camp-id-card-wrapper" style="width: 336px; max-width: 336px; height: 432px; max-height: 432px; box-sizing: border-box; background: #ffffff; border: 1.5px solid #cbd5e1; border-radius: 14px; overflow: hidden; display: flex; flex-direction: column; justify-content: space-between; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif; margin: 0 auto; text-align: left; box-shadow: 0 4px 6px -1px rgba(0,0,0,0.1); user-select: text;">
      <!-- Top Accent Bar -->
      <div style="height: 4px; width: 100%; background: linear-gradient(to right, #f59e0b, #10b981, #2563eb); flex-shrink: 0;"></div>

      <!-- Card Header (Increased Size) -->
      <div style="background: #0f172a; color: #ffffff; padding: 6px 12px; display: flex; align-items: center; justify-content: space-between; border-bottom: 1.5px solid rgba(245, 158, 11, 0.5); flex-shrink: 0; height: 48px; box-sizing: border-box;">
        <img src="${parasportsLogo}" alt="AP Para Sports" style="height: 34px; width: 34px; object-fit: contain; background: rgba(255,255,255,0.12); border-radius: 50%; padding: 2px; flex-shrink: 0;" />
        <div style="text-align: center; flex: 1; padding: 0 6px; min-width: 0;">
          <div style="font-size: 10px; font-weight: 900; text-transform: uppercase; color: #fcd34d; line-height: 1.15; letter-spacing: 0.2px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">SAMAGRA SHIKSHA • 60 DAYS COACHING CAMP</div>
          <div style="font-size: 8.5px; font-weight: 700; color: #ffffff; text-transform: uppercase; margin-top: 2px; line-height: 1; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">Para Sports Association of Andhra Pradesh</div>
          <div style="font-size: 7px; color: #cbd5e1; font-weight: 600; margin-top: 2px; line-height: 1; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">Affiliated to Paralympic Committee of India (PCI) &amp; SAAP</div>
        </div>
        <img src="${ssaLogo}" alt="Samagra Shiksha" style="height: 34px; width: 34px; object-fit: contain; background: rgba(255,255,255,0.12); border-radius: 50%; padding: 2px; flex-shrink: 0;" />
      </div>

      <!-- Role Banner Ribbon -->
      <div style="background: ${roleGradient}; color: #ffffff; padding: 3px 12px; display: flex; align-items: center; justify-content: space-between; flex-shrink: 0; height: 20px; box-sizing: border-box;">
        <span style="font-size: 10px; font-weight: 900; text-transform: uppercase; letter-spacing: 0.5px; color: #fef08a;">${roleTitle}</span>
        <span style="font-size: 8px; font-weight: 800; padding: 1px 7px; border-radius: 9999px; background: rgba(255,255,255,0.2); color: #ffffff; border: 1px solid rgba(255,255,255,0.3); text-transform: uppercase; letter-spacing: 0.5px;">RESIDENTIAL CAMP</span>
      </div>

      <!-- Card Body -->
      <div style="padding: 7px 10px 5px 10px; flex: 1; display: flex; flex-direction: column; justify-content: space-between; background: linear-gradient(to bottom, #f8fafc, #ffffff, #f8fafc); min-height: 0; box-sizing: border-box;">
        <div>
          <!-- Top Row: Photo + Name + Discipline -->
          <div style="display: flex; gap: 10px; align-items: flex-start;">
            <div style="display: flex; flex-direction: column; align-items: center; flex-shrink: 0;">
              <div style="width: 76px; height: 92px; border-radius: 10px; overflow: hidden; border: 2px solid ${roleBorderColor}; background: #f1f5f9; padding: 1px; box-sizing: border-box;">
                <img src="${photoUrl}" alt="${name}" style="width: 100%; height: 100%; object-fit: cover; border-radius: 8px; display: block;" onerror="this.src='${origin}/images/default-user.png';" />
              </div>
              <span style="margin-top: 3px; font-size: 7.5px; font-weight: 800; color: #ffffff; background: ${roleBadgeBg}; padding: 1px 6px; border-radius: 9999px; text-transform: uppercase; letter-spacing: 0.5px; display: block; text-align: center; max-width: 76px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">${roleTag}</span>
            </div>

            <div style="flex: 1; min-width: 0; padding-top: 2px;">
              <div style="font-size: ${nameFontSize}; font-weight: 900; color: #0f172a; text-transform: uppercase; line-height: 1.15; word-break: break-word;">${name}</div>
              ${fatherName ? `<div style="font-size: 9px; color: #475569; font-weight: 600; margin-top: 2px; line-height: 1.2;">S/o, D/o: <span style="color: #1e293b; font-weight: 700;">${fatherName}</span></div>` : ''}
              ${isOfficial ? `<div style="font-size: 9px; color: #475569; font-weight: 600; margin-top: 2px; line-height: 1.2;">Duty: <span style="color: #065f46; font-weight: 700;">${card.duty_role || "Official"}</span></div>` : ''}
              ${isCoach ? `<div style="font-size: 9px; color: #475569; font-weight: 600; margin-top: 2px; line-height: 1.2;">Staff: <span style="color: #9a3412; font-weight: 700;">${card.designation || "Assistant Coach"}</span></div>` : ''}

              ${isAthlete ? `
                <div style="margin-top: 4px; display: flex; flex-wrap: wrap; gap: 4px; align-items: center;">
                  <span style="background: #eff6ff; border: 1px solid #bfdbfe; border-radius: 4px; padding: 2px 6px; font-size: 8.5px; font-weight: 700; color: #1e3a8a; line-height: 1;">${athleteSport}</span>
                  <span style="background: ${athleteCategory !== '—' ? '#faf5ff' : '#f1f5f9'}; border: 1px solid ${athleteCategory !== '—' ? '#d8b4fe' : '#cbd5e1'}; border-radius: 4px; padding: 2px 6px; font-size: 8.5px; font-weight: 800; color: ${athleteCategory !== '—' ? '#581c87' : '#94a3b8'}; line-height: 1;">Category: ${athleteCategory}</span>
                </div>
              ` : ''}
            </div>
          </div>

          <!-- Metadata Box -->
          <div style="margin-top: 5px; background: #ffffff; border: 1px solid #e2e8f0; border-radius: 8px; padding: 5px 8px; box-sizing: border-box;">
            <table style="width: 100%; border-collapse: collapse; table-layout: fixed;">
              <tr>
                <td style="width: 50%; vertical-align: top; padding: 2px 4px 2px 0;">
                  <span style="font-size: 7px; text-transform: uppercase; letter-spacing: 0.5px; color: #94a3b8; font-weight: 700; display: block; line-height: 1; margin-bottom: 2px;">${idLabel}</span>
                  <span style="font-size: 9.5px; font-weight: 900; font-family: monospace; color: #0f172a; display: block; line-height: 1.1; word-break: break-all;">${idVal}</span>
                </td>
                <td style="width: 50%; vertical-align: top; padding: 2px 0 2px 4px;">
                  <span style="font-size: 7px; text-transform: uppercase; letter-spacing: 0.5px; color: #94a3b8; font-weight: 700; display: block; line-height: 1; margin-bottom: 2px;">${secondaryLabel}</span>
                  <span style="font-size: 9.5px; font-weight: 900; font-family: monospace; color: #0f172a; display: block; line-height: 1.1; word-break: break-word;">${secondaryVal}</span>
                </td>
              </tr>
              <tr>
                <td style="width: 50%; vertical-align: top; padding: 2px 4px 2px 0; border-top: 1px solid #f1f5f9;">
                  <span style="font-size: 7px; text-transform: uppercase; letter-spacing: 0.5px; color: #94a3b8; font-weight: 700; display: block; line-height: 1; margin-bottom: 2px;">DISTRICT</span>
                  <span style="font-size: 9px; font-weight: 700; color: #0f172a; display: block; line-height: 1.1; word-break: break-word;">${district}</span>
                </td>
                <td style="width: 50%; vertical-align: top; padding: 2px 0 2px 4px; border-top: 1px solid #f1f5f9;">
                  <span style="font-size: 7px; text-transform: uppercase; letter-spacing: 0.5px; color: #94a3b8; font-weight: 700; display: block; line-height: 1; margin-bottom: 2px;">CONTACT NO</span>
                  <span style="font-size: 9px; font-weight: 700; font-family: monospace; color: #0f172a; display: block; line-height: 1.1;">${mobile}</span>
                </td>
              </tr>
              <tr>
                <td style="width: 50%; vertical-align: top; padding: 2px 4px 2px 0; border-top: 1px solid #f1f5f9;">
                  <span style="font-size: 7px; text-transform: uppercase; letter-spacing: 0.5px; color: #94a3b8; font-weight: 700; display: block; line-height: 1; margin-bottom: 2px;">DATE OF BIRTH</span>
                  <span style="font-size: 9px; font-weight: 700; color: #0f172a; display: block; line-height: 1.1;">${dob}</span>
                </td>
                <td style="width: 50%; vertical-align: top; padding: 2px 0 2px 4px; border-top: 1px solid #f1f5f9;">
                  <span style="font-size: 7px; text-transform: uppercase; letter-spacing: 0.5px; color: #94a3b8; font-weight: 700; display: block; line-height: 1; margin-bottom: 2px;">GENDER</span>
                  <span style="font-size: 9px; font-weight: 700; color: #0f172a; display: block; line-height: 1.1;">${gender}</span>
                </td>
              </tr>
              ${showTeamAndCoach ? `
              <tr>
                <td style="width: 50%; vertical-align: top; padding: 2px 4px 2px 0; border-top: 1px solid #f1f5f9;">
                  <span style="font-size: 7px; text-transform: uppercase; letter-spacing: 0.5px; color: #94a3b8; font-weight: 700; display: block; line-height: 1; margin-bottom: 2px;">TEAM</span>
                  <span style="font-size: 8.5px; font-weight: 700; color: ${teamName === '—' ? '#94a3b8' : '#0f172a'}; display: block; line-height: 1.1; word-break: break-word;">${teamName}</span>
                </td>
                <td style="width: 50%; vertical-align: top; padding: 2px 0 2px 4px; border-top: 1px solid #f1f5f9;">
                  <span style="font-size: 7px; text-transform: uppercase; letter-spacing: 0.5px; color: #94a3b8; font-weight: 700; display: block; line-height: 1; margin-bottom: 2px;">ASSISTANT COACH</span>
                  <span style="font-size: 8.5px; font-weight: 700; color: ${coachName === '—' ? '#94a3b8' : '#0f172a'}; display: block; line-height: 1.1; word-break: break-word;">${coachName}</span>
                </td>
              </tr>
              ` : ''}
            </table>
          </div>
        </div>

        <!-- Bottom Segment -->
        <div style="margin-top: 4px;">
          <!-- Dates, Venue & District Strip -->
          <div style="background: #f1f5f9; border: 1px solid #cbd5e1; border-radius: 6px; padding: 3px 6px; display: flex; justify-content: space-between; align-items: center; font-size: 7.5px; font-weight: 700; color: #1e293b; line-height: 1.1;">
            <span style="white-space: nowrap;"><strong style="color: #0369a1;">DATES:</strong> ${campDates}</span>
            <span style="font-weight: 800; text-transform: uppercase; color: #0f172a; text-align: right; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; max-width: 180px; margin-left: 4px;" title="${campInfo.venue} (${campInfo.district})"><strong style="color: #b91c1c;">VENUE:</strong> ${campInfo.venue} &bull; <strong>DIST:</strong> ${campInfo.district}</span>
          </div>

          <!-- Official signatures, positioned above the security footer -->
          <div style="margin-top: 4px; border-top: 1px solid #e2e8f0; padding-top: 3px; display: flex; justify-content: space-between; align-items: flex-end; gap: 8px;">
            <!-- Left: Official -->
            <div style="text-align: center; display: flex; flex-direction: column; align-items: center; width: 135px;">
              <div style="height: 26px; width: 100%; display: flex; align-items: flex-end; justify-content: center; margin-bottom: 2px; overflow: hidden;">
                ${activePresSig ? `<img src="${activePresSig}" alt="Official Signature" style="max-height: 26px; max-width: 100%; width: auto; height: auto; object-fit: contain; object-position: bottom center; display: block;" />` : ``}
              </div>
              <div style="width: 100%; border-top: 1px solid #94a3b8; padding-top: 2px; line-height: 1;">
                <div style="font-size: 7.5px; font-weight: 900; color: #0f172a; white-space: nowrap;">Official</div>
                <div style="font-size: 6.5px; font-weight: 800; text-transform: uppercase; color: #1e3a8a; margin-top: 2px; white-space: nowrap;">SAMAGRA SHIKSHA ABHIYAN</div>
              </div>
            </div>

            <!-- Right: Secretary -->
            <div style="text-align: center; display: flex; flex-direction: column; align-items: center; width: 135px;">
              <div style="height: 26px; width: 100%; display: flex; align-items: flex-end; justify-content: center; margin-bottom: 2px; overflow: hidden;">
                ${activeSecSig ? `<img src="${activeSecSig}" alt="Secretary Signature" style="max-height: 26px; max-width: 100%; width: auto; height: auto; object-fit: contain; object-position: bottom center; display: block;" />` : ``}
              </div>
              <div style="width: 100%; border-top: 1px solid #94a3b8; padding-top: 2px; line-height: 1;">
                <div style="font-size: 7.5px; font-weight: 900; color: #0f172a; white-space: nowrap;">V Ramaswamy</div>
                <div style="font-size: 6.5px; font-weight: 800; text-transform: uppercase; color: #1e3a8a; margin-top: 2px; white-space: nowrap;">SECRETARY, PSAAP</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Bottom Security Pass Footer -->
      <div style="background: #0f172a; color: #fcd34d; padding: 3px 8px; font-size: 7px; font-weight: 700; letter-spacing: 0.5px; text-transform: uppercase; display: flex; justify-content: space-between; align-items: center; border-top: 1px solid rgba(245, 158, 11, 0.3); flex-shrink: 0; line-height: 1;">
        <span>SECURITY PASS • RESIDENTIAL CAMP</span>
        <span>VALID FOR DURATION ONLY</span>
      </div>
    </div>
  `;
}

/**
 * Render PAN card size ID card HTML (Custom Standard: 100mm x 70mm horizontal)
 */
export function renderPanCardHtml(card, camp, currentSignature, showTeamAndCoach, origin = "") {
  const isAthlete = card._cardType === "athlete";
  const isCoach = card._cardType === "coach";
  const isOfficial = card._cardType === "official";

  const name = card.name || "Participant Name";
  const fatherName =
    card.father_name ||
    card.relation ||
    (isOfficial || isCoach ? "" : "Guardian");
  const district = resolveParticipantDistrict(card);
  const mobile = resolveParticipantMobile(card);
  const teamName = resolveParticipantTeam(card);
  const coachName = resolveParticipantCoach(card);
  const dob = resolveParticipantDob(card);
  const gender = resolveParticipantGender(card);

  let photoUrl = getCardPhoto(
    card.photo ||
    card.player?.photo ||
    card.player_photo ||
    card.profile_photo ||
    card.user?.photo
  );
  if (photoUrl && photoUrl.startsWith("/") && origin) {
    photoUrl = origin + photoUrl;
  }
  const parasportsLogo = (origin ? origin : "") + "/images/parasports.png";
  const ssaLogo = (origin ? origin : "") + "/images/ssa.png";

  let roleTitle = "CAMP ATHLETE";
  let roleGradient = "linear-gradient(to right, #1e40af, #312e81, #0f172a)";
  let roleBorderColor = "#4f46e5";
  let roleBadgeBg = "#4f46e5";
  let roleTag = "ATHLETE";
  let idLabel = "ID NUMBER";
  let idVal = card.id_number && String(card.id_number).trim() !== "" ? String(card.id_number).trim() : "—";
  let secondaryLabel = "TEAM";
  let secondaryVal = teamName || "—";
  let athleteSport = "Para Sports";
  let athleteCategory = "—";

  if (isAthlete) {
    const rawDisc =
      card?.discipline_name ||
      card?.discipline?.name ||
      card?.player?.discipline_name ||
      "";
    if (!rawDisc || !rawDisc.trim()) {
      athleteSport = "Para Sports";
    } else if (/athletics/i.test(rawDisc)) {
      athleteSport = "Athletics";
    } else {
      athleteSport = rawDisc.trim();
    }
    athleteCategory = resolveParticipantCategory(card);
  } else if (isCoach) {
    roleTitle = "CAMP ASST COACH";
    roleGradient = "linear-gradient(to right, #b45309, #9a3412, #451a03)";
    roleBorderColor = "#d97706";
    roleBadgeBg = "#d97706";
    roleTag = "ASST COACH";
    idLabel = "COACH ID";
    idVal = card.coach_id || `CCH-${String(card.id || 1).padStart(3, "0")}`;
    secondaryLabel = "ASSIGNMENT";
    secondaryVal = card.team_name || "Coaching Staff";
  } else if (isOfficial) {
    roleTitle = card.designation || "CAMP OFFICIAL";
    roleGradient = "linear-gradient(to right, #065f46, #134e4a, #022c22)";
    roleBorderColor = "#059669";
    roleBadgeBg = "#059669";
    roleTag = card.duty_role || "OFFICIAL";
    idLabel = "OFFICIAL ID";
    idVal = card.official_id_card_no || `OFF-${String(card.id || 1).padStart(3, "0")}`;
    secondaryLabel = card.room_no ? "ROOM NO" : "DUTY ROLE";
    secondaryVal = card.room_no ? `Room ${card.room_no}` : (card.duty_role || "Official");
  }

  const nameLength = (name || "").length;
  let nameFontSize = "12px";
  if (nameLength > 28) nameFontSize = "10px";
  else if (nameLength > 20) nameFontSize = "11px";

  const campDates = formatCampDates(camp);
  const campInfo = resolveCampVenueAndDistrict(camp, card);
  const { secretary: activeSecSig, president: activePresSig } = resolveCardSignatures(currentSignature);

  return `
    <div class="pan-id-card-wrapper" style="width: 378px; max-width: 378px; height: 265px; max-height: 265px; box-sizing: border-box; background: #ffffff; border: 1.5px solid #cbd5e1; border-radius: 12px; overflow: hidden; display: flex; flex-direction: column; justify-content: space-between; font-family: Arial, Helvetica, sans-serif; margin: 0 auto; text-align: left; box-shadow: 0 4px 6px -1px rgba(0,0,0,0.1); position: relative; user-select: text;">
      <!-- Top Accent Bar -->
      <div style="height: 3px; width: 100%; background: linear-gradient(to right, #f59e0b, #10b981, #2563eb); flex-shrink: 0;"></div>

      <!-- Card Header -->
      <div style="background: #0f172a; color: #ffffff; padding: 3px 8px; display: flex; align-items: center; justify-content: space-between; border-bottom: 1.5px solid rgba(245, 158, 11, 0.5); flex-shrink: 0; min-height: 36px; height: 36px; box-sizing: border-box;">
        <img src="${parasportsLogo}" alt="AP Para Sports" style="height: 26px; width: 26px; object-fit: contain; background: rgba(255,255,255,0.12); border-radius: 50%; padding: 1.5px; flex-shrink: 0;" />
        <div style="text-align: center; flex: 1; padding: 0 4px; min-width: 0;">
          <div style="font-size: 9px; font-weight: 900; text-transform: uppercase; color: #fcd34d; line-height: 1.25; letter-spacing: 0.1px; white-space: nowrap;">SAMAGRA SHIKSHA • 60 DAYS COACHING CAMP</div>
          <div style="font-size: 7.5px; font-weight: 700; color: #ffffff; text-transform: uppercase; margin-top: 1px; line-height: 1.25; white-space: nowrap;">Para Sports Association of AP &bull; PCI &amp; SAAP</div>
        </div>
        <img src="${ssaLogo}" alt="Samagra Shiksha" style="height: 26px; width: 26px; object-fit: contain; background: rgba(255,255,255,0.12); border-radius: 50%; padding: 1.5px; flex-shrink: 0;" />
      </div>

      <!-- Role Banner Ribbon -->
      <div style="background: ${roleGradient}; color: #ffffff; padding: 0 10px; display: flex; align-items: center; justify-content: space-between; flex-shrink: 0; min-height: 16px; height: 16px; box-sizing: border-box;">
        <span style="font-size: 8.5px; font-weight: 900; text-transform: uppercase; letter-spacing: 0.5px; color: #fef08a; line-height: 16px;">${roleTitle}</span>
        <span style="font-size: 7px; font-weight: 800; padding: 1px 6px; border-radius: 9999px; background: rgba(255,255,255,0.2); color: #ffffff; border: 1px solid rgba(255,255,255,0.3); text-transform: uppercase; line-height: 1.2;">100mm &times; 70mm</span>
      </div>

      <!-- Card Body -->
      <div style="padding: 3px 8px; flex: 1; min-height: 0; display: flex; gap: 7px; align-items: flex-start; background: linear-gradient(to bottom, #f8fafc, #ffffff, #f8fafc); box-sizing: border-box;">
        <!-- Left Photo Column -->
        <div style="display: flex; flex-direction: column; align-items: center; flex-shrink: 0; width: 60px;">
          <div style="width: 58px; height: 70px; border-radius: 6px; overflow: hidden; border: 2px solid ${roleBorderColor}; background: #f1f5f9; padding: 1px; box-sizing: border-box;">
            <img src="${photoUrl}" alt="${name}" style="width: 100%; height: 100%; object-fit: cover; border-radius: 4px; display: block;" onerror="this.src='${origin}/images/default-user.png';" />
          </div>
          <span style="margin-top: 2px; font-size: 6.5px; font-weight: 800; color: #ffffff; background: ${roleBadgeBg}; padding: 1px 5px; border-radius: 9999px; text-transform: uppercase; letter-spacing: 0.5px; display: block; text-align: center; max-width: 58px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; line-height: 1.2;">${roleTag}</span>
        </div>

        <!-- Right Data Column -->
        <div style="flex: 1; min-width: 0; display: flex; flex-direction: column; justify-content: flex-start; gap: 1.5px;">
          <!-- Name & Primary Detail -->
          <div>
            <div style="font-size: ${nameFontSize}; font-weight: 900; color: #0f172a; text-transform: uppercase; line-height: 1.2; word-break: break-word;" title="${name}">${name}</div>
            ${fatherName ? `<div style="font-size: 8px; color: #475569; font-weight: 600; line-height: 1.2; margin-top: 1px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">S/o, D/o: <span style="color: #1e293b; font-weight: 700;">${fatherName}</span></div>` : ''}
            ${isOfficial ? `<div style="font-size: 8px; color: #475569; font-weight: 600; line-height: 1.2; margin-top: 1px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">Duty: <span style="color: #065f46; font-weight: 700;">${card.duty_role || "Official"}</span></div>` : ''}
            ${isCoach ? `<div style="font-size: 8px; color: #475569; font-weight: 600; line-height: 1.2; margin-top: 1px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">Staff: <span style="color: #9a3412; font-weight: 700;">${card.designation || "Assistant Coach"}</span></div>` : ''}
          </div>

          <!-- Micro Fields Grid -->
          <div style="background: #ffffff; border: 1px solid #cbd5e1; border-radius: 6px; padding: 2px 4px; box-sizing: border-box;">
            <table style="width: 100%; border-collapse: collapse; table-layout: fixed;">
              <tr>
                <td style="width: 50%; vertical-align: top; padding: 1px 3px;">
                  <div style="font-size: 6.5px; text-transform: uppercase; letter-spacing: 0.5px; color: #64748b; font-weight: 700; line-height: 1.25;">${idLabel}</div>
                  <div style="font-size: 8.5px; font-weight: 900; font-family: Arial, monospace; color: #0f172a; line-height: 1.25; word-break: break-word;">${idVal}</div>
                </td>
                <td style="width: 50%; vertical-align: top; padding: 1px 3px;">
                  <div style="font-size: 6.5px; text-transform: uppercase; letter-spacing: 0.5px; color: #64748b; font-weight: 700; line-height: 1.25;">${secondaryLabel}</div>
                  <div style="font-size: 8.5px; font-weight: 800; color: #b45309; line-height: 1.25; word-break: break-word;" title="${secondaryVal}">${secondaryVal}</div>
                </td>
              </tr>
              <tr>
                <td style="width: 50%; vertical-align: top; padding: 1px 3px; border-top: 1px solid #f1f5f9;">
                  <div style="font-size: 6.5px; text-transform: uppercase; letter-spacing: 0.5px; color: #64748b; font-weight: 700; line-height: 1.25;">DISTRICT</div>
                  <div style="font-size: 8px; font-weight: 700; color: #0f172a; line-height: 1.25; word-break: break-word;" title="${district}">${district}</div>
                </td>
                <td style="width: 50%; vertical-align: top; padding: 1px 3px; border-top: 1px solid #f1f5f9;">
                  <div style="font-size: 6.5px; text-transform: uppercase; letter-spacing: 0.5px; color: #64748b; font-weight: 700; line-height: 1.25;">CONTACT NO</div>
                  <div style="font-size: 8px; font-weight: 700; font-family: Arial, monospace; color: #0f172a; line-height: 1.25;">${mobile}</div>
                </td>
              </tr>
              <tr>
                <td style="width: 50%; vertical-align: top; padding: 1px 3px; border-top: 1px solid #f1f5f9;">
                  <div style="font-size: 6.5px; text-transform: uppercase; letter-spacing: 0.5px; color: #64748b; font-weight: 700; line-height: 1.25;">DOB</div>
                  <div style="font-size: 8px; font-weight: 700; color: #0f172a; line-height: 1.25;">${dob}</div>
                </td>
                <td style="width: 50%; vertical-align: top; padding: 1px 3px; border-top: 1px solid #f1f5f9;">
                  <div style="font-size: 6.5px; text-transform: uppercase; letter-spacing: 0.5px; color: #64748b; font-weight: 700; line-height: 1.25;">GENDER</div>
                  <div style="font-size: 8px; font-weight: 700; color: #0f172a; line-height: 1.25;">${gender}</div>
                </td>
              </tr>
              ${isAthlete ? `
              <tr>
                <td colspan="2" style="vertical-align: top; padding: 1.5px 3px; border-top: 1px solid #f1f5f9;">
                  <div style="display: flex; gap: 4px; align-items: center; flex-wrap: wrap; line-height: 1.25;">
                    <span style="font-size: 7.5px; font-weight: 700; color: #1e3a8a; background: #eff6ff; border: 1px solid #bfdbfe; border-radius: 3px; padding: 1px 4px; line-height: 1.2;">${athleteSport}</span>
                    <span style="font-size: 7.5px; font-weight: 800; color: ${athleteCategory !== '—' ? '#581c87' : '#64748b'}; background: ${athleteCategory !== '—' ? '#faf5ff' : '#f1f5f9'}; border: 1px solid ${athleteCategory !== '—' ? '#d8b4fe' : '#cbd5e1'}; border-radius: 3px; padding: 1px 4px; line-height: 1.2;">Cat: ${athleteCategory}</span>
                    ${showTeamAndCoach && coachName !== '—' ? `<span style="font-size: 7px; color: #475569; font-weight: 600; line-height: 1.2;">Coach: <strong style="color: #0f172a;">${coachName}</strong></span>` : ''}
                  </div>
                </td>
              </tr>
              ` : ''}
            </table>
          </div>
        </div>
      </div>

      <!-- Venue Beside Camp Dates Strip -->
      <div style="background: #f1f5f9; border-top: 1px solid #cbd5e1; border-bottom: 1px solid #cbd5e1; padding: 1.5px 8px; display: flex; justify-content: space-between; align-items: center; font-size: 7.5px; font-weight: 700; color: #1e293b; line-height: 1.25; flex-shrink: 0; min-height: 16px; height: 16px; box-sizing: border-box;">
        <span style="white-space: nowrap; line-height: 1.25;"><strong style="color: #0369a1;">DATES:</strong> ${campDates}</span>
        <span style="text-align: right; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; max-width: 220px; color: #0f172a; line-height: 1.25;" title="${campInfo.venue} (${campInfo.district})"><strong style="color: #b91c1c;">VENUE:</strong> ${campInfo.venue} &bull; <strong>DIST:</strong> ${campInfo.district}</span>
      </div>

      <!-- Signatures Row: Official on Left, Secretary on Right with Space to Sign Above -->
      <div style="padding: 1px 12px 2px 12px; background: #ffffff; display: flex; justify-content: space-between; align-items: flex-end; flex-shrink: 0; box-sizing: border-box;">
        <!-- Left: Official, Samagra Siksha Abhiyan -->
        <div style="text-align: center; width: 140px; display: flex; flex-direction: column; align-items: center;">
          <div style="height: 25px; width: 100%; display: flex; align-items: flex-end; justify-content: center; overflow: hidden; margin-bottom: 2px;">
            ${activePresSig ? `<img src="${activePresSig}" alt="Official Signature" style="max-height: 25px; max-width: 100%; width: auto; height: auto; object-fit: contain; object-position: bottom center; display: block;" />` : ''}
          </div>
          <div style="border-top: 1px solid #94a3b8; width: 100%; padding-top: 2px; line-height: 1.2;">
            <div style="font-size: 7.5px; font-weight: 800; color: #0f172a; text-transform: uppercase;">Official</div>
            <div style="font-size: 6.5px; font-weight: 700; color: #1e3a8a; text-transform: uppercase;">Samagra Siksha Abhiyan</div>
          </div>
        </div>

        <!-- Right: V Ramaswamy, Secretary, Para Sports Association of Andhra Pradesh -->
        <div style="text-align: center; width: 175px; display: flex; flex-direction: column; align-items: center;">
          <div style="height: 25px; width: 100%; display: flex; align-items: flex-end; justify-content: center; overflow: hidden; margin-bottom: 2px;">
            ${activeSecSig ? `<img src="${activeSecSig}" alt="Secretary Signature" style="max-height: 25px; max-width: 100%; width: auto; height: auto; object-fit: contain; object-position: bottom center; display: block;" />` : ''}
          </div>
          <div style="border-top: 1px solid #94a3b8; width: 100%; padding-top: 2px; line-height: 1.2;">
            <div style="font-size: 7.5px; font-weight: 800; color: #0f172a;">V Ramaswamy</div>
            <div style="font-size: 6px; font-weight: 700; color: #1e3a8a; text-transform: uppercase; white-space: nowrap;">Secretary, Para Sports Association of Andhra Pradesh</div>
          </div>
        </div>
      </div>

      <!-- Footer Blue Line: Just Para Sports Association of Andhra Pradesh -->
      <div style="background: #1e3a8a; color: #ffffff; padding: 2px 8px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; min-height: 16px; height: 16px; box-sizing: border-box; line-height: 16px; border-top: 1px solid rgba(255, 255, 255, 0.2);">
        <span style="font-size: 7.5px; font-weight: 800; letter-spacing: 0.8px; text-transform: uppercase; color: #fcd34d;">
          PARA SPORTS ASSOCIATION OF ANDHRA PRADESH
        </span>
      </div>
    </div>
  `;
}

/**
 * Generate standalone printable HTML for single PAN Card (100mm x 70mm, centered on A4 page)
 */
export function generateSinglePanCardSheetHtml(card, camp, currentSignature, showTeamAndCoach, origin = "") {
  const panHtml = renderPanCardHtml(card, camp, currentSignature, showTeamAndCoach, origin);
  return `
    <!DOCTYPE html>
    <html>
      <head>
        <meta charset="utf-8" />
        <title>Camp ID Card (100x70mm) - ${card.name || "Participant"}</title>
        <style>
          @page {
            size: A4 portrait;
            margin: 10mm;
          }
          * {
            box-sizing: border-box;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          html, body {
            margin: 0;
            padding: 0;
            background: #ffffff;
            color: #000000;
            font-family: Arial, Helvetica, sans-serif;
          }
          .print-page {
            width: 794px;
            height: 1122px;
            max-height: 1122px;
            box-sizing: border-box;
            display: flex;
            align-items: center;
            justify-content: center;
            background: #ffffff;
            padding: 20px;
            margin: 0 auto;
          }
          @media print {
            body {
              margin: 0 !important;
              padding: 0 !important;
            }
            .print-page {
              width: 198mm !important;
              height: 285mm !important;
              max-height: 285mm !important;
              padding: 0 !important;
            }
          }
        </style>
      </head>
      <body>
        <div class="print-page">
          <div style="border: 1.5px dashed #94a3b8; border-radius: 14px; padding: 20px 24px; background: #fbfcfe; text-align: center; display: inline-flex; flex-direction: column; align-items: center; box-shadow: 0 2px 4px rgba(0,0,0,0.04);">
            <div style="font-size: 8.5px; font-weight: 800; color: #64748b; letter-spacing: 0.5px; text-transform: uppercase; margin-bottom: 12px; font-family: Arial, sans-serif;">
              ID CARD SIZE: 100mm &times; 70mm
            </div>

            <!-- The PAN Card -->
            ${panHtml}

            <div style="margin-top: 12px; font-size: 9px; font-weight: 700; color: #64748b; letter-spacing: 0.5px; text-transform: uppercase; font-family: Arial, sans-serif;">
              ✂ CUT ALONG DASHED LINE &bull; 100mm &times; 70mm &bull; WALLET / LAMINATED PASS
            </div>
          </div>
        </div>
      </body>
    </html>
  `;
}

/**
 * Generate printable HTML for batch PAN Cards (8 cards per A4 page: 2 cols x 4 rows @ 100mm x 70mm)
 */
export function generatePanCardBatchSheetsHtml(cards, camp, currentSignature, showTeamAndCoach, origin = "") {
  let pagesHtml = "";
  const totalPages = Math.ceil(cards.length / 8);

  for (let p = 0; p < totalPages; p++) {
    const chunk = cards.slice(p * 8, p * 8 + 8);
    let rowsHtml = "";

    for (let r = 0; r < 4; r++) {
      const idxLeft = r * 2;
      const idxRight = r * 2 + 1;
      const cLeft = chunk[idxLeft] ? renderPanCardHtml(chunk[idxLeft], camp, currentSignature, showTeamAndCoach, origin) : "";
      const cRight = chunk[idxRight] ? renderPanCardHtml(chunk[idxRight], camp, currentSignature, showTeamAndCoach, origin) : "";

      rowsHtml += `
        <tr style="height: 25%;">
          <td style="width: 50%; vertical-align: middle; text-align: center; padding: 2px; margin: 0; overflow: hidden; border: 1px dashed #e2e8f0;">
            ${cLeft}
          </td>
          <td style="width: 50%; vertical-align: middle; text-align: center; padding: 2px; margin: 0; overflow: hidden; border: 1px dashed #e2e8f0;">
            ${cRight}
          </td>
        </tr>
      `;
    }

    pagesHtml += `
      <div class="print-page" style="width: 794px; height: 1122px; max-height: 1122px; page-break-after: always; break-after: page; box-sizing: border-box; padding: 8px 6px; margin: 0 auto; overflow: hidden; background: #ffffff;">
        <div style="font-size: 8px; font-weight: 700; color: #94a3b8; text-transform: uppercase; letter-spacing: 0.5px; text-align: center; margin-bottom: 4px;">
          COACHING CAMP ID CARDS &bull; 100mm &times; 70mm (8 PER SHEET) &bull; SHEET ${p + 1} OF ${totalPages}
        </div>
        <table style="width: 100%; height: calc(100% - 16px); border-collapse: separate; border-spacing: 4px; table-layout: fixed; margin: 0; padding: 0;">
          ${rowsHtml}
        </table>
      </div>
    `;
  }

  return `
    <!DOCTYPE html>
    <html>
      <head>
        <meta charset="utf-8" />
        <title>Camp ID Cards (100x70mm - 8 Per Sheet A4)</title>
        <style>
          @page {
            size: A4 portrait;
            margin: 3mm;
          }
          * {
            box-sizing: border-box;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          html, body {
            margin: 0;
            padding: 0;
            background: #ffffff;
            color: #000000;
          }
          .print-page {
            box-sizing: border-box;
            page-break-inside: avoid;
            break-inside: avoid;
          }
          .print-page:last-child {
            page-break-after: auto !important;
            break-after: auto !important;
          }
          @media print {
            body {
              margin: 0 !important;
              padding: 0 !important;
            }
            .print-page {
              width: 204mm !important;
              height: 291mm !important;
              max-height: 291mm !important;
              padding: 1mm !important;
              page-break-after: always !important;
              break-after: page !important;
            }
            .print-page:last-child {
              page-break-after: auto !important;
              break-after: auto !important;
            }
            .pan-id-card-wrapper {
              width: 98mm !important;
              max-width: 98mm !important;
              height: 69mm !important;
              max-height: 69mm !important;
            }
          }
        </style>
      </head>
      <body>
        ${pagesHtml}
      </body>
    </html>
  `;
}

/**
 * Generate printable HTML for batch PAN Cards (1 card centered per A4 page @ 100mm x 70mm)
 */
export function generatePanSinglePerPageSheetsHtml(cards, camp, currentSignature, showTeamAndCoach, origin = "") {
  let pagesHtml = "";

  for (let i = 0; i < cards.length; i++) {
    const card = cards[i];
    const panHtml = renderPanCardHtml(card, camp, currentSignature, showTeamAndCoach, origin);

    pagesHtml += `
      <div class="print-page" style="width: 794px; height: 1122px; max-height: 1122px; page-break-after: always; break-after: page; box-sizing: border-box; display: flex; flex-direction: column; align-items: center; justify-content: center; margin: 0 auto; overflow: hidden; background: #ffffff; padding: 20px;">
        <div style="border: 1.5px dashed #94a3b8; border-radius: 14px; padding: 20px 24px; background: #fbfcfe; text-align: center; display: inline-flex; flex-direction: column; align-items: center; box-shadow: 0 2px 4px rgba(0,0,0,0.04);">
          <div style="font-size: 8.5px; font-weight: 800; color: #64748b; letter-spacing: 0.5px; text-transform: uppercase; margin-bottom: 12px; font-family: Arial, sans-serif;">
            ID CARD SIZE: 100mm &times; 70mm
          </div>

          <!-- The PAN Card -->
          ${panHtml}

          <div style="margin-top: 12px; font-size: 9px; font-weight: 700; color: #64748b; letter-spacing: 0.5px; text-transform: uppercase; font-family: Arial, sans-serif;">
            ✂ CUT ALONG DASHED LINE &bull; 100mm &times; 70mm &bull; CARD ${i + 1} OF ${cards.length}
          </div>
        </div>
      </div>
    `;
  }

  return `
    <!DOCTYPE html>
    <html>
      <head>
        <meta charset="utf-8" />
        <title>Camp ID Cards (100x70mm - Single Per Page)</title>
        <style>
          @page {
            size: A4 portrait;
            margin: 6mm;
          }
          * {
            box-sizing: border-box;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          html, body {
            margin: 0;
            padding: 0;
            background: #ffffff;
            color: #000000;
            font-family: Arial, Helvetica, sans-serif;
          }
          .print-page {
            box-sizing: border-box;
            page-break-inside: avoid;
            break-inside: avoid;
          }
          .print-page:last-child {
            page-break-after: auto !important;
            break-after: auto !important;
          }
          @media print {
            body {
              margin: 0 !important;
              padding: 0 !important;
            }
            .print-page {
              width: 198mm !important;
              height: 285mm !important;
              max-height: 285mm !important;
              padding: 6mm !important;
              page-break-after: always !important;
              break-after: page !important;
            }
            .print-page:last-child {
              page-break-after: auto !important;
              break-after: auto !important;
            }
          }
        </style>
      </head>
      <body>
        ${pagesHtml}
      </body>
    </html>
  `;
}

/**
 * Render Full Page A4 ID Card standalone HTML string (Official Accreditation / Display / Poster size)
 */
export function renderA4CardHtml(card, camp, currentSignature, showTeamAndCoach, origin = "") {
  const isAthlete = card._cardType === "athlete";
  const isCoach = card._cardType === "coach";
  const isOfficial = card._cardType === "official";

  const name = card.name || "Participant Name";
  const fatherName =
    card.father_name ||
    card.relation ||
    (isOfficial || isCoach ? "" : "Guardian");
  const district = resolveParticipantDistrict(card);
  const mobile = resolveParticipantMobile(card);
  const teamName = resolveParticipantTeam(card);
  const coachName = resolveParticipantCoach(card);
  const dob = resolveParticipantDob(card);
  const gender = resolveParticipantGender(card);

  let photoUrl = getCardPhoto(
    card.photo ||
    card.player?.photo ||
    card.player_photo ||
    card.profile_photo ||
    card.user?.photo
  );
  if (photoUrl && photoUrl.startsWith("/") && origin) {
    photoUrl = origin + photoUrl;
  }
  const parasportsLogo = (origin ? origin : "") + "/images/parasports.png";
  const ssaLogo = (origin ? origin : "") + "/images/ssa.png";

  let roleTitle = "CAMP ATHLETE";
  let roleGradient = "linear-gradient(to right, #1e40af, #312e81, #0f172a)";
  let roleBorderColor = "#4f46e5";
  let roleBadgeBg = "#4f46e5";
  let roleTag = "ATHLETE";
  let idLabel = "ID NUMBER";
  let idVal = card.id_number && String(card.id_number).trim() !== "" ? String(card.id_number).trim() : "—";
  let secondaryLabel = "TEAM / SQUAD";
  let secondaryVal = teamName || "—";
  let athleteSport = "Para Sports";
  let athleteCategory = "—";

  if (isAthlete) {
    const rawDisc =
      card?.discipline_name ||
      card?.discipline?.name ||
      card?.player?.discipline_name ||
      "";
    if (!rawDisc || !rawDisc.trim()) {
      athleteSport = "Para Sports";
    } else if (/athletics/i.test(rawDisc)) {
      athleteSport = "Athletics";
    } else {
      athleteSport = rawDisc.trim();
    }
    athleteCategory = resolveParticipantCategory(card);
  } else if (isCoach) {
    roleTitle = "CAMP ASSISTANT COACH";
    roleGradient = "linear-gradient(to right, #b45309, #9a3412, #451a03)";
    roleBorderColor = "#d97706";
    roleBadgeBg = "#d97706";
    roleTag = "ASSISTANT COACH";
    idLabel = "COACH ID";
    idVal = card.coach_id || `CCH-${String(card.id || 1).padStart(3, "0")}`;
    secondaryLabel = "ASSIGNMENT";
    secondaryVal = card.team_name || "Coaching Staff";
  } else if (isOfficial) {
    roleTitle = card.designation || "CAMP OFFICIAL";
    roleGradient = "linear-gradient(to right, #065f46, #134e4a, #022c22)";
    roleBorderColor = "#059669";
    roleBadgeBg = "#059669";
    roleTag = card.duty_role || "OFFICIAL";
    idLabel = "OFFICIAL ID";
    idVal = card.official_id_card_no || `OFF-${String(card.id || 1).padStart(3, "0")}`;
    secondaryLabel = card.room_no ? "ROOM NO" : "DUTY ROLE";
    secondaryVal = card.room_no ? `Room ${card.room_no}` : (card.duty_role || "Official");
  }

  const campDates = formatCampDates(camp);
  const campInfo = resolveCampVenueAndDistrict(camp, card);
  const { secretary: activeSecSig, president: activePresSig } = resolveCardSignatures(currentSignature);

  return `
    <div class="a4-id-card-wrapper" style="width: 100%; max-width: 720px; height: 1010px; max-height: 1010px; box-sizing: border-box; background: #ffffff; border: 3px solid #1e293b; border-radius: 20px; overflow: hidden; display: flex; flex-direction: column; justify-content: space-between; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif; margin: 0 auto; text-align: left; box-shadow: 0 10px 25px -5px rgba(0,0,0,0.12); position: relative; user-select: text;">
      <!-- Top Accent Bar -->
      <div style="height: 6px; width: 100%; background: linear-gradient(to right, #f59e0b, #10b981, #2563eb); flex-shrink: 0;"></div>

      <!-- Card Header (Noticeably Enlarged Official Size) -->
      <div style="background: #0f172a; color: #ffffff; padding: 10px 20px; display: flex; align-items: center; justify-content: space-between; border-bottom: 2px solid rgba(245, 158, 11, 0.5); flex-shrink: 0; height: 68px; box-sizing: border-box;">
        <img src="${parasportsLogo}" alt="AP Para Sports" style="height: 54px; width: 54px; object-fit: contain; background: rgba(255,255,255,0.12); border-radius: 50%; padding: 3px; flex-shrink: 0;" />
        <div style="text-align: center; flex: 1; padding: 0 14px; min-width: 0;">
          <div style="font-size: 16px; font-weight: 900; text-transform: uppercase; color: #fcd34d; letter-spacing: 0.5px; line-height: 1.2;">SAMAGRA SHIKSHA &bull; 60 DAYS RESIDENTIAL COACHING CAMP</div>
          <div style="font-size: 13.5px; font-weight: 800; color: #ffffff; text-transform: uppercase; margin-top: 3px; letter-spacing: 0.2px;">Para Sports Association of Andhra Pradesh</div>
          <div style="font-size: 10.5px; color: #cbd5e1; font-weight: 600; margin-top: 2px;">Affiliated to Paralympic Committee of India (PCI) &amp; Sports Authority of Andhra Pradesh (SAAP)</div>
        </div>
        <img src="${ssaLogo}" alt="Samagra Shiksha" style="height: 54px; width: 54px; object-fit: contain; background: rgba(255,255,255,0.12); border-radius: 50%; padding: 3px; flex-shrink: 0;" />
      </div>

      <!-- Role Banner Ribbon -->
      <div style="background: ${roleGradient}; color: #ffffff; padding: 7px 20px; display: flex; align-items: center; justify-content: space-between; flex-shrink: 0;">
        <span style="font-size: 14px; font-weight: 900; text-transform: uppercase; letter-spacing: 1px; color: #fef08a;">${roleTitle}</span>
        <span style="font-size: 10px; font-weight: 800; padding: 2px 10px; border-radius: 9999px; background: rgba(255,255,255,0.2); color: #ffffff; border: 1px solid rgba(255,255,255,0.3); text-transform: uppercase; letter-spacing: 0.5px;">RESIDENTIAL COACHING CAMP</span>
      </div>

      <!-- Card Body -->
      <div style="padding: 16px 24px; flex: 1; display: flex; flex-direction: column; justify-content: space-between; background: linear-gradient(to bottom, #f8fafc, #ffffff, #f8fafc); min-height: 0; box-sizing: border-box;">
        <div>
          <!-- Top Row: Large Photo + Name + Primary Info -->
          <div style="display: flex; gap: 20px; align-items: flex-start;">
            <!-- Photo Frame -->
            <div style="display: flex; flex-direction: column; align-items: center; flex-shrink: 0;">
              <div style="width: 140px; height: 175px; border-radius: 14px; overflow: hidden; border: 3px solid ${roleBorderColor}; background: #f1f5f9; padding: 2px; box-sizing: border-box; box-shadow: 0 4px 6px -1px rgba(0,0,0,0.1);">
                <img src="${photoUrl}" alt="${name}" style="width: 100%; height: 100%; object-fit: cover; border-radius: 10px; display: block;" onerror="this.src='${origin}/images/default-user.png';" />
              </div>
              <span style="margin-top: 6px; font-size: 11px; font-weight: 900; color: #ffffff; background: ${roleBadgeBg}; padding: 3px 14px; border-radius: 9999px; text-transform: uppercase; letter-spacing: 1px; display: block; text-align: center; max-width: 140px; box-shadow: 0 2px 4px rgba(0,0,0,0.1);">${roleTag}</span>
            </div>

            <!-- Name & Meta -->
            <div style="flex: 1; min-width: 0; padding-top: 4px;">
              <div style="font-size: 23px; font-weight: 900; color: #0f172a; text-transform: uppercase; line-height: 1.15; word-break: break-word;">${name}</div>
              ${fatherName ? `<div style="font-size: 14px; color: #475569; font-weight: 600; margin-top: 5px; line-height: 1.2;">S/o, D/o: <span style="color: #1e293b; font-weight: 700;">${fatherName}</span></div>` : ''}
              ${isOfficial ? `<div style="font-size: 14px; color: #475569; font-weight: 600; margin-top: 5px; line-height: 1.2;">Duty: <span style="color: #065f46; font-weight: 800;">${card.duty_role || "Official"}</span></div>` : ''}
              ${isCoach ? `<div style="font-size: 14px; color: #475569; font-weight: 600; margin-top: 5px; line-height: 1.2;">Staff: <span style="color: #9a3412; font-weight: 800;">${card.designation || "Assistant Coach"}</span></div>` : ''}

              <!-- Badges -->
              <div style="margin-top: 10px; display: flex; flex-wrap: wrap; gap: 8px; align-items: center;">
                <span style="background: #eff6ff; border: 1.5px solid #bfdbfe; border-radius: 6px; padding: 4px 12px; font-size: 12.5px; font-weight: 800; color: #1e3a8a; line-height: 1;">${athleteSport}</span>
                <span style="background: ${athleteCategory !== '—' ? '#faf5ff' : '#f1f5f9'}; border: 1.5px solid ${athleteCategory !== '—' ? '#d8b4fe' : '#cbd5e1'}; border-radius: 6px; padding: 4px 12px; font-size: 12.5px; font-weight: 800; color: ${athleteCategory !== '—' ? '#581c87' : '#94a3b8'}; line-height: 1;">Category: ${athleteCategory}</span>
              </div>
              ${showTeamAndCoach && coachName !== '—' ? `<div style="margin-top: 8px;"><span style="background: #f8fafc; border: 1.5px solid #e2e8f0; border-radius: 6px; padding: 4px 12px; font-size: 12px; font-weight: 700; color: #334155; line-height: 1; display: inline-block;">Coach: <strong style="color: #0f172a;">${coachName}</strong></span></div>` : ''}
            </div>
          </div>

          <!-- Metadata Box (Large A4) -->
          <div style="margin-top: 16px; background: #ffffff; border: 2px solid #e2e8f0; border-radius: 14px; padding: 12px 18px; box-sizing: border-box; box-shadow: 0 2px 4px rgba(0,0,0,0.03);">
            <table style="width: 100%; border-collapse: collapse; table-layout: fixed;">
              <tr>
                <td style="width: 50%; vertical-align: top; padding: 6px 10px;">
                  <span style="font-size: 9px; text-transform: uppercase; letter-spacing: 0.8px; color: #64748b; font-weight: 800; display: block; margin-bottom: 2px;">${idLabel}</span>
                  <span style="font-size: 18px; font-weight: 900; font-family: monospace; color: #0f172a; display: block; line-height: 1.1;">${idVal}</span>
                </td>
                <td style="width: 50%; vertical-align: top; padding: 6px 10px;">
                  <span style="font-size: 9px; text-transform: uppercase; letter-spacing: 0.8px; color: #64748b; font-weight: 800; display: block; margin-bottom: 2px;">${secondaryLabel}</span>
                  <span style="font-size: 17px; font-weight: 900; color: #b45309; display: block; line-height: 1.1;">${secondaryVal}</span>
                </td>
              </tr>
              <tr>
                <td style="width: 50%; vertical-align: top; padding: 8px 10px 4px 10px; border-top: 1.5px solid #f1f5f9;">
                  <span style="font-size: 9px; text-transform: uppercase; letter-spacing: 0.8px; color: #64748b; font-weight: 800; display: block; margin-bottom: 2px;">HOME DISTRICT</span>
                  <span style="font-size: 15px; font-weight: 800; color: #0f172a; display: block; line-height: 1.1;">${district}</span>
                </td>
                <td style="width: 50%; vertical-align: top; padding: 8px 10px 4px 10px; border-top: 1.5px solid #f1f5f9;">
                  <span style="font-size: 9px; text-transform: uppercase; letter-spacing: 0.8px; color: #64748b; font-weight: 800; display: block; margin-bottom: 2px;">CONTACT / MOBILE NO</span>
                  <span style="font-size: 15px; font-weight: 800; font-family: monospace; color: #0f172a; display: block; line-height: 1.1;">${mobile}</span>
                </td>
              </tr>
              <tr>
                <td style="width: 50%; vertical-align: top; padding: 8px 10px 4px 10px; border-top: 1.5px solid #f1f5f9;"><span style="font-size: 9px; text-transform: uppercase; letter-spacing: 0.8px; color: #64748b; font-weight: 800; display: block; margin-bottom: 2px;">DATE OF BIRTH</span><span style="font-size: 15px; font-weight: 800; color: #0f172a; display: block; line-height: 1.1;">${dob}</span></td>
                <td style="width: 50%; vertical-align: top; padding: 8px 10px 4px 10px; border-top: 1.5px solid #f1f5f9;"><span style="font-size: 9px; text-transform: uppercase; letter-spacing: 0.8px; color: #64748b; font-weight: 800; display: block; margin-bottom: 2px;">GENDER</span><span style="font-size: 15px; font-weight: 800; color: #0f172a; display: block; line-height: 1.1;">${gender}</span></td>
              </tr>
            </table>
          </div>

          <!-- Camp Venue & Schedule Box (PAN Card Model) -->
          <div style="margin-top: 12px; background: #f1f5f9; border: 1.5px solid #cbd5e1; border-radius: 12px; padding: 10px 18px; display: flex; justify-content: space-between; align-items: center; font-size: 13px; font-weight: 700; color: #1e293b;">
            <span><strong style="color: #0369a1;">DATES:</strong> ${campDates}</span>
            <span><strong style="color: #b91c1c;">VENUE:</strong> ${campInfo.venue} &bull; <strong>DIST:</strong> ${campInfo.district}</span>
          </div>
        </div>

        <!-- Official Verification Notice -->
        <div style="margin-top: 8px; padding: 8px 14px; background: #eff6ff; border: 1px dashed #93c5fd; border-radius: 10px; text-align: center; font-size: 11px; color: #1e3a8a; font-weight: 600;">
          This official accreditation is issued by Para Sports Association of Andhra Pradesh for the Residential Coaching Camp supported by Samagra Shiksha.
        </div>
      </div>

      <!-- Signatures Row: Official on Left, Secretary on Right (PAN Card Model) -->
      <div style="padding: 6px 36px 12px 36px; background: #ffffff; display: flex; justify-content: space-between; align-items: flex-end; flex-shrink: 0; box-sizing: border-box;">
        <!-- Left: Official, Samagra Siksha Abhiyan -->
        <div style="text-align: center; width: 230px; display: flex; flex-direction: column; align-items: center;">
          <div style="height: 48px; width: 100%; display: flex; align-items: flex-end; justify-content: center; overflow: hidden; margin-bottom: 4px;">
            ${activePresSig ? `<img src="${activePresSig}" alt="Official Signature" style="max-height: 48px; max-width: 100%; width: auto; height: auto; object-fit: contain; object-position: bottom center; display: block;" />` : ''}
          </div>
          <div style="border-top: 1.5px solid #94a3b8; width: 100%; padding-top: 4px; line-height: 1.25;">
            <div style="font-size: 13px; font-weight: 800; color: #0f172a; text-transform: uppercase;">Official</div>
            <div style="font-size: 10.5px; font-weight: 700; color: #1e3a8a; text-transform: uppercase;">Samagra Siksha Abhiyan</div>
          </div>
        </div>

        <!-- Right: V Ramaswamy, Secretary, Para Sports Association of Andhra Pradesh -->
        <div style="text-align: center; width: 290px; display: flex; flex-direction: column; align-items: center;">
          <div style="height: 48px; width: 100%; display: flex; align-items: flex-end; justify-content: center; overflow: hidden; margin-bottom: 4px;">
            ${activeSecSig ? `<img src="${activeSecSig}" alt="Secretary Signature" style="max-height: 48px; max-width: 100%; width: auto; height: auto; object-fit: contain; object-position: bottom center; display: block;" />` : ''}
          </div>
          <div style="border-top: 1.5px solid #94a3b8; width: 100%; padding-top: 4px; line-height: 1.25;">
            <div style="font-size: 13px; font-weight: 800; color: #0f172a;">V Ramaswamy</div>
            <div style="font-size: 10px; font-weight: 700; color: #1e3a8a; text-transform: uppercase; white-space: nowrap;">Secretary, Para Sports Association of Andhra Pradesh</div>
          </div>
        </div>
      </div>

      <!-- Footer Blue Line: Exactly like PAN card model -->
      <div style="background: #1e3a8a; color: #ffffff; padding: 6px 16px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; min-height: 30px; height: 30px; box-sizing: border-box; line-height: 30px; border-top: 1px solid rgba(255, 255, 255, 0.2);">
        <span style="font-size: 12px; font-weight: 800; letter-spacing: 1.2px; text-transform: uppercase; color: #fcd34d;">
          PARA SPORTS ASSOCIATION OF ANDHRA PRADESH
        </span>
      </div>
    </div>
  `;
}

/**
 * Generate standalone printable HTML for single ID card set to full A4 page
 */
export function generateSingleA4CardSheetHtml(card, camp, currentSignature, showTeamAndCoach, origin = "") {
  const cardHtml = renderA4CardHtml(card, camp, currentSignature, showTeamAndCoach, origin);
  return `
    <!DOCTYPE html>
    <html>
      <head>
        <meta charset="utf-8" />
        <title>Camp A4 ID Card - ${card.name || "Participant"}</title>
        <style>
          @page {
            size: A4 portrait;
            margin: 6mm;
          }
          * {
            box-sizing: border-box;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          html, body {
            margin: 0;
            padding: 0;
            background: #ffffff;
            color: #000000;
          }
          .print-page {
            width: 794px;
            height: 1122px;
            max-height: 1122px;
            box-sizing: border-box;
            display: flex;
            align-items: center;
            justify-content: center;
            background: #ffffff;
            padding: 10px;
            margin: 0 auto;
          }
          @media print {
            body {
              margin: 0 !important;
              padding: 0 !important;
            }
            .print-page {
              width: 198mm !important;
              height: 285mm !important;
              max-height: 285mm !important;
              padding: 0 !important;
            }
          }
        </style>
      </head>
      <body>
        <div class="print-page">
          ${cardHtml}
        </div>
      </body>
    </html>
  `;
}

/**
 * Generate printable HTML for batch A4 ID Cards (1 full A4 card per page)
 */
export function generateA4BatchSheetsHtml(cards, camp, currentSignature, showTeamAndCoach, origin = "") {
  let pagesHtml = "";

  for (let i = 0; i < cards.length; i++) {
    const card = cards[i];
    const cardHtml = renderA4CardHtml(card, camp, currentSignature, showTeamAndCoach, origin);

    pagesHtml += `
      <div class="print-page" style="width: 794px; height: 1122px; max-height: 1122px; page-break-after: always; break-after: page; box-sizing: border-box; display: flex; flex-direction: column; align-items: center; justify-content: center; margin: 0 auto; overflow: hidden; background: #ffffff; padding: 10px;">
        ${cardHtml}
      </div>
    `;
  }

  return `
    <!DOCTYPE html>
    <html>
      <head>
        <meta charset="utf-8" />
        <title>Camp ID Cards (A4 Size Full Sheet)</title>
        <style>
          @page {
            size: A4 portrait;
            margin: 6mm;
          }
          * {
            box-sizing: border-box;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          html, body {
            margin: 0;
            padding: 0;
            background: #ffffff;
            color: #000000;
          }
          .print-page {
            box-sizing: border-box;
            page-break-inside: avoid;
            break-inside: avoid;
          }
          .print-page:last-child {
            page-break-after: auto !important;
            break-after: auto !important;
          }
          @media print {
            body {
              margin: 0 !important;
              padding: 0 !important;
            }
            .print-page {
              width: 198mm !important;
              height: 285mm !important;
              max-height: 285mm !important;
              padding: 0 !important;
              page-break-after: always !important;
              break-after: page !important;
            }
            .print-page:last-child {
              page-break-after: auto !important;
              break-after: auto !important;
            }
          }
        </style>
      </head>
      <body>
        ${pagesHtml}
      </body>
    </html>
  `;
}

/**
 * Generate standalone printable HTML for single ID card
 */
export function generateSingleCardSheetHtml(card, camp, currentSignature, showTeamAndCoach, origin = "") {
  const cardHtml = renderIdCardHtml(card, camp, currentSignature, showTeamAndCoach, origin);
  return `
    <!DOCTYPE html>
    <html>
      <head>
        <meta charset="utf-8" />
        <title>Camp ID Card - ${card.name || "Participant"}</title>
        <style>
          @page {
            size: A4 portrait;
            margin: 8mm;
          }
          * {
            box-sizing: border-box;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          html, body {
            margin: 0;
            padding: 0;
            background: #ffffff;
            color: #000000;
          }
          .print-page {
            width: 794px;
            height: 1122px;
            max-height: 1122px;
            box-sizing: border-box;
            display: flex;
            align-items: center;
            justify-content: center;
            background: #ffffff;
            padding: 20px;
            margin: 0 auto;
          }
          @media print {
            .print-page {
              width: 198mm !important;
              height: 285mm !important;
            }
          }
        </style>
      </head>
      <body>
        <div class="print-page">
          <div style="border: 1.5px dashed #94a3b8; border-radius: 18px; padding: 18px 24px 16px 24px; background: #fbfcfe; text-align: center; display: inline-flex; flex-direction: column; align-items: center; box-shadow: 0 2px 4px rgba(0,0,0,0.04);">
            <!-- Lanyard Slot Punch Guide -->
            <div style="display: flex; align-items: center; justify-content: center; gap: 6px; margin-bottom: 12px; font-size: 8px; font-weight: 800; color: #64748b; letter-spacing: 0.5px; text-transform: uppercase;">
              <span style="display: inline-block; width: 22px; height: 6px; border: 1.5px solid #64748b; border-radius: 4px; background: #ffffff;"></span>
              <span>PUNCH HOLE / LANYARD CLIP</span>
            </div>

            <!-- The ID Card -->
            ${cardHtml}

            <!-- Cutting & Lamination Note -->
            <div style="margin-top: 12px; font-size: 9px; font-weight: 700; color: #64748b; letter-spacing: 0.5px; text-transform: uppercase; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;">
              ✂ CUT ALONG DASHED LINE &bull; LAMINATION POUCH SIZE: 95mm &times; 135mm &bull; OFFICIAL CAMP PASS
            </div>
          </div>
        </div>
      </body>
    </html>
  `;
}

/**
 * Generate standalone printable HTML for single ID card per page (1 card per A4 page for all cards)
 */
export function generateSinglePerPageSheetsHtml(cards, camp, currentSignature, showTeamAndCoach, origin = "") {
  let pagesHtml = "";

  for (let i = 0; i < cards.length; i++) {
    const card = cards[i];
    const cardHtml = renderIdCardHtml(card, camp, currentSignature, showTeamAndCoach, origin);

    pagesHtml += `
      <div class="print-page" style="width: 794px; height: 1122px; max-height: 1122px; page-break-after: always; break-after: page; box-sizing: border-box; display: flex; flex-direction: column; align-items: center; justify-content: center; margin: 0 auto; overflow: hidden; background: #ffffff; padding: 20px;">
        <div style="border: 1.5px dashed #94a3b8; border-radius: 18px; padding: 18px 24px 16px 24px; background: #fbfcfe; text-align: center; display: inline-flex; flex-direction: column; align-items: center; box-shadow: 0 2px 4px rgba(0,0,0,0.04);">
          <!-- Lanyard Slot Punch Guide -->
          <div style="display: flex; align-items: center; justify-content: center; gap: 6px; margin-bottom: 12px; font-size: 8px; font-weight: 800; color: #64748b; letter-spacing: 0.5px; text-transform: uppercase;">
            <span style="display: inline-block; width: 22px; height: 6px; border: 1.5px solid #64748b; border-radius: 4px; background: #ffffff;"></span>
            <span>PUNCH HOLE / LANYARD CLIP</span>
          </div>

          <!-- The ID Card -->
          ${cardHtml}

          <!-- Cutting & Lamination Note -->
          <div style="margin-top: 12px; font-size: 9px; font-weight: 700; color: #64748b; letter-spacing: 0.5px; text-transform: uppercase; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;">
            ✂ CUT ALONG DASHED LINE &bull; LAMINATION POUCH: 95mm &times; 135mm &bull; CARD ${i + 1} OF ${cards.length}
          </div>
        </div>
      </div>
    `;
  }

  return `
    <!DOCTYPE html>
    <html>
      <head>
        <meta charset="utf-8" />
        <title>Camp ID Cards (Single Card Per Page)</title>
        <style>
          @page {
            size: A4 portrait;
            margin: 6mm;
          }
          * {
            box-sizing: border-box;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          html, body {
            margin: 0;
            padding: 0;
            background: #ffffff;
            color: #000000;
          }
          .print-page {
            box-sizing: border-box;
            page-break-inside: avoid;
            break-inside: avoid;
          }
          .print-page:last-child {
            page-break-after: auto !important;
            break-after: auto !important;
          }
          @media print {
            body {
              margin: 0 !important;
              padding: 0 !important;
            }
            .print-page {
              width: 198mm !important;
              height: 285mm !important;
              max-height: 285mm !important;
              padding: 6mm !important;
              page-break-after: always !important;
              break-after: page !important;
            }
            .print-page:last-child {
              page-break-after: auto !important;
              break-after: auto !important;
            }
          }
        </style>
      </head>
      <body>
        ${pagesHtml}
      </body>
    </html>
  `;
}

/**
 * Generate standalone printable HTML for batch 2x2 ID cards (exactly 4 per A4 page)
 */
export function generate2x2SheetsHtml(cards, camp, currentSignature, showTeamAndCoach, origin = "") {
  let pagesHtml = "";
  const totalPages = Math.ceil(cards.length / 4);

  for (let p = 0; p < totalPages; p++) {
    const chunk = cards.slice(p * 4, p * 4 + 4);
    const c0 = chunk[0] ? renderIdCardHtml(chunk[0], camp, currentSignature, showTeamAndCoach, origin) : "";
    const c1 = chunk[1] ? renderIdCardHtml(chunk[1], camp, currentSignature, showTeamAndCoach, origin) : "";
    const c2 = chunk[2] ? renderIdCardHtml(chunk[2], camp, currentSignature, showTeamAndCoach, origin) : "";
    const c3 = chunk[3] ? renderIdCardHtml(chunk[3], camp, currentSignature, showTeamAndCoach, origin) : "";

    pagesHtml += `
      <div class="print-page" style="width: 794px; height: 1122px; max-height: 1122px; page-break-after: always; break-after: page; box-sizing: border-box; padding: 16px 12px; margin: 0 auto; overflow: hidden; background: #ffffff;">
        <table style="width: 100%; height: 100%; border-collapse: separate; border-spacing: 12px 10px; table-layout: fixed; margin: 0; padding: 0;">
          <tr style="height: 50%;">
            <td style="width: 50%; height: 50%; vertical-align: middle; text-align: center; padding: 0; margin: 0; overflow: hidden;">
              ${c0}
            </td>
            <td style="width: 50%; height: 50%; vertical-align: middle; text-align: center; padding: 0; margin: 0; overflow: hidden;">
              ${c1}
            </td>
          </tr>
          <tr style="height: 50%;">
            <td style="width: 50%; height: 50%; vertical-align: middle; text-align: center; padding: 0; margin: 0; overflow: hidden;">
              ${c2}
            </td>
            <td style="width: 50%; height: 50%; vertical-align: middle; text-align: center; padding: 0; margin: 0; overflow: hidden;">
              ${c3}
            </td>
          </tr>
        </table>
      </div>
    `;
  }

  return `
    <!DOCTYPE html>
    <html>
      <head>
        <meta charset="utf-8" />
        <title>Camp ID Cards (2x2 A4)</title>
        <style>
          @page {
            size: A4 portrait;
            margin: 6mm;
          }
          * {
            box-sizing: border-box;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          html, body {
            margin: 0;
            padding: 0;
            background: #ffffff;
            color: #000000;
          }
          .print-page {
            box-sizing: border-box;
            page-break-inside: avoid;
            break-inside: avoid;
          }
          .print-page:last-child {
            page-break-after: auto !important;
            break-after: auto !important;
          }
          @media print {
            body {
              margin: 0 !important;
              padding: 0 !important;
            }
            .print-page {
              width: 198mm !important;
              height: 285mm !important;
              max-height: 285mm !important;
              padding: 2mm !important;
              page-break-after: always !important;
              break-after: page !important;
            }
            .print-page:last-child {
              page-break-after: auto !important;
              break-after: auto !important;
            }
          }
        </style>
      </head>
      <body>
        ${pagesHtml}
      </body>
    </html>
  `;
}

/**
 * Single ID Card View (Standard 100mm x 70mm horizontal)
 */
export const SinglePanCard = ({
  data,
  type = "athlete",
  camp = null,
  secretarySignature = null,
  signatures = null,
  onEditIdNumber = null,
  showTeamAndCoach = false,
}) => {
  const isAthlete = type === "athlete";
  const isCoach = type === "coach";
  const isOfficial = type === "official";

  const campDates = formatCampDates(camp);
  const campInfo = resolveCampVenueAndDistrict(camp, data);

  // Data mapping
  const name = data?.name || "Participant Name";
  const fatherName =
    data?.father_name ||
    data?.relation ||
    (isOfficial || isCoach ? "" : "Guardian");
  const district = resolveParticipantDistrict(data);
  const mobile = resolveParticipantMobile(data);
  const teamName = resolveParticipantTeam(data);
  const coachName = resolveParticipantCoach(data);
  const dob = resolveParticipantDob(data);
  const gender = resolveParticipantGender(data);
  const photoUrl = getCardPhoto(
    data?.photo ||
    data?.player?.photo ||
    data?.player_photo ||
    data?.profile_photo ||
    data?.user?.photo
  );

  const sigs = resolveCardSignatures(signatures || secretarySignature);
  const activePresSig = signatures?.president || sigs.president;
  const activeSecSig = signatures?.secretary || sigs.secretary;

  let roleTitle = "CAMP ATHLETE";
  let roleColor = "from-blue-800 via-indigo-900 to-slate-900";
  let roleBorder = "border-indigo-600";
  let roleBadgeBg = "bg-indigo-600";
  let roleTag = "ATHLETE";
  let RoleIcon = FaRunning;
  let idLabel = "ID NUMBER";
  let idNumberDisplay =
    data?.id_number && String(data.id_number).trim() !== ""
      ? String(data.id_number).trim()
      : "—";
  let secondaryLabel = "TEAM";
  let secondaryValue = teamName || "—";
  let athleteSport = "Para Sports";
  let athleteCategory = "—";

  if (isAthlete) {
    roleTitle = "CAMP ATHLETE";
    roleColor = "from-blue-800 via-indigo-900 to-slate-900";
    roleBorder = "border-indigo-600";
    roleBadgeBg = "bg-indigo-600";
    roleTag = "ATHLETE";
    RoleIcon = FaRunning;
    idLabel = "ID NUMBER";
    secondaryLabel = "TEAM";
    secondaryValue = teamName || "—";

    const rawDisc =
      data?.discipline_name ||
      data?.discipline?.name ||
      data?.player?.discipline_name ||
      "";
    if (!rawDisc || !rawDisc.trim()) {
      athleteSport = "Para Sports";
    } else if (/athletics/i.test(rawDisc)) {
      athleteSport = "Athletics";
    } else {
      athleteSport = rawDisc.trim();
    }
    athleteCategory = resolveParticipantCategory(data);
  } else if (isCoach) {
    roleTitle = "CAMP ASST COACH";
    roleColor = "from-amber-700 via-orange-800 to-amber-950";
    roleBorder = "border-amber-500";
    roleBadgeBg = "bg-amber-600";
    roleTag = "ASST COACH";
    RoleIcon = FaUserShield;
    idLabel = "COACH ID";
    idNumberDisplay = data?.coach_id || `CCH-${String(data?.id || 1).padStart(3, "0")}`;
    secondaryLabel = "ASSIGNMENT";
    secondaryValue = data?.team_name || "Coaching Staff";
  } else if (isOfficial) {
    roleTitle = data?.designation || "CAMP OFFICIAL";
    roleColor = "from-emerald-800 via-teal-900 to-slate-950";
    roleBorder = "border-emerald-500";
    roleBadgeBg = "bg-emerald-600";
    roleTag = data?.duty_role || "OFFICIAL";
    RoleIcon = FaUserTie;
    idLabel = "OFFICIAL ID";
    idNumberDisplay = data?.official_id_card_no || `OFF-${String(data?.id || 1).padStart(3, "0")}`;
    secondaryLabel = data?.room_no ? "ROOM NO" : "DUTY ROLE";
    secondaryValue = data?.room_no ? `Room ${data.room_no}` : (data?.duty_role || "Official");
  }

  const nameLength = (name || "").length;
  let nameFontSize = "11.5px";
  if (nameLength > 28) nameFontSize = "9.5px";
  else if (nameLength > 20) nameFontSize = "10.5px";

  return (
    <div
      className="pan-id-card-wrapper bg-white shadow-xl rounded-xl overflow-hidden border border-slate-300 relative flex flex-col justify-between select-text"
      style={{
        width: "100%",
        maxWidth: "378px",
        height: "265px",
        minHeight: "265px",
        maxHeight: "265px",
        boxSizing: "border-box",
        fontFamily: "Arial, Helvetica, sans-serif",
      }}
    >
      {/* Top Accent Bar */}
      <div className="h-[3px] w-full bg-gradient-to-r from-amber-400 via-emerald-500 to-blue-600 shrink-0" />

      {/* Card Header */}
      <div className="bg-slate-900 text-white px-2 py-1 flex items-center justify-between border-b border-amber-400/40 shrink-0 min-h-[38px] h-[38px] box-border">
        <img
          src="/images/parasports.png"
          alt="AP Para Sports"
          className="h-7 w-7 object-contain bg-white/10 rounded-full p-0.5 shrink-0"
          onError={(e) => { e.target.style.display = "none"; }}
        />
        <div className="text-center flex-1 px-1 min-w-0">
          <p className="text-[9.5px] font-black uppercase tracking-tight text-amber-300 leading-snug whitespace-nowrap">
            SAMAGRA SHIKSHA • 60 DAYS COACHING CAMP
          </p>
          <p className="text-[8px] font-bold text-slate-100 uppercase tracking-tight mt-0.5 leading-snug whitespace-nowrap">
            Para Sports Association of AP &bull; PCI &amp; SAAP
          </p>
        </div>
        <img
          src="/images/ssa.png"
          alt="Samagra Shiksha"
          className="h-7 w-7 object-contain bg-white/10 rounded-full p-0.5 shrink-0"
          onError={(e) => { e.target.style.display = "none"; }}
        />
      </div>

      {/* Role Banner Ribbon */}
      <div className={`bg-gradient-to-r ${roleColor} text-white px-2.5 flex items-center justify-between shadow-inner shrink-0 min-h-[17px] h-[17px] box-border`}>
        <div className="flex items-center gap-1 min-w-0">
          <RoleIcon className="text-amber-300 text-[9px] shrink-0" />
          <span className="text-[8.5px] font-black uppercase tracking-wider text-amber-200 truncate leading-[17px]">
            {roleTitle}
          </span>
        </div>
        <span className="text-[7px] font-extrabold px-1.5 py-0.5 rounded-full bg-white/20 text-white tracking-wider border border-white/30 uppercase shrink-0 leading-none">
          100mm × 70mm PASS
        </span>
      </div>

      {/* Card Body */}
      <div className="px-2 py-1 flex-1 flex gap-2 items-start bg-gradient-to-b from-slate-50/80 via-white to-slate-50/80 text-slate-800 min-h-0 box-border">
        {/* Left Photo Column */}
        <div className="flex flex-col items-center shrink-0 w-[62px]">
          <div
            style={{ width: "60px", height: "72px" }}
            className={`rounded-lg overflow-hidden border-2 ${roleBorder} shadow-xs bg-slate-100 flex items-center justify-center p-0.5`}
          >
            <img
              src={photoUrl}
              alt={name}
              className="w-full h-full object-cover rounded-md"
              onError={(e) => {
                if (e.target.src !== DEFAULT_AVATAR && !e.target.src.endsWith(DEFAULT_AVATAR)) {
                  e.target.src = DEFAULT_AVATAR;
                }
              }}
            />
          </div>
          <span className={`mt-1 text-[6.5px] font-extrabold text-white px-1.5 py-0.5 rounded-full shadow-xs uppercase tracking-wider ${roleBadgeBg} max-w-[60px] truncate leading-tight`}>
            {roleTag}
          </span>
        </div>

        {/* Right Details Column */}
        <div className="flex-1 min-w-0 flex flex-col justify-start gap-0.5">
          <div>
            <h3
              className="font-black text-slate-900 uppercase break-words"
              style={{ fontSize: nameFontSize, lineHeight: "1.25" }}
              title={name}
            >
              {name}
            </h3>
            {fatherName ? (
              <p className="text-[8px] text-slate-600 font-semibold truncate mt-0.5 leading-snug">
                S/o, D/o: <span className="text-slate-800 font-bold">{fatherName}</span>
              </p>
            ) : isOfficial ? (
              <p className="text-[8px] text-slate-600 font-semibold truncate mt-0.5 leading-snug">
                Duty: <span className="text-emerald-900 font-bold">{data?.duty_role || "Official"}</span>
              </p>
            ) : isCoach ? (
              <p className="text-[8px] text-slate-600 font-semibold truncate mt-0.5 leading-snug">
                Staff: <span className="text-amber-900 font-bold">{data?.designation || "Assistant Coach"}</span>
              </p>
            ) : null}
          </div>

          {/* Grid Metadata */}
          <div className="bg-white/95 rounded-lg p-1 border border-slate-200 grid grid-cols-2 gap-x-2 gap-y-0.5 shadow-xs">
            {/* ID Number */}
            <div
              className={`min-w-0 ${onEditIdNumber && isAthlete ? "cursor-pointer hover:bg-slate-200/60 rounded px-0.5 transition-colors" : ""}`}
              onClick={() => onEditIdNumber && isAthlete && onEditIdNumber(data)}
              title={onEditIdNumber && isAthlete ? "Click to manually edit ID Number & Team" : undefined}
            >
              <span className="text-[6.5px] uppercase tracking-wider text-slate-500 font-bold block leading-tight">
                {idLabel}
              </span>
              <span className="font-black font-mono text-[8.5px] text-indigo-950 block leading-snug truncate">
                {idNumberDisplay}
              </span>
            </div>

            {/* Team / Assignment */}
            <div className="min-w-0">
              <span className="text-[6.5px] uppercase tracking-wider text-slate-500 font-bold block leading-tight">
                {secondaryLabel}
              </span>
              <span className="font-bold text-[8.5px] text-amber-900 block leading-snug truncate" title={secondaryValue}>
                {secondaryValue}
              </span>
            </div>

            {/* District */}
            <div className="min-w-0 border-t border-slate-200/80 pt-0.5">
              <span className="text-[6.5px] uppercase tracking-wider text-slate-500 font-bold block leading-tight">
                DISTRICT
              </span>
              <span className="font-bold text-[8px] text-slate-900 block leading-snug truncate" title={district}>
                {district}
              </span>
            </div>

            {/* Contact No */}
            <div className="min-w-0 border-t border-slate-200/80 pt-0.5">
              <span className="text-[6.5px] uppercase tracking-wider text-slate-500 font-bold block leading-tight">
                CONTACT NO
              </span>
              <span className="font-bold font-mono text-[8px] text-slate-900 block leading-snug truncate">
                {mobile}
              </span>
            </div>

            {/* DOB & Gender */}
            <div className="min-w-0 border-t border-slate-200/80 pt-0.5">
              <span className="text-[6.5px] uppercase tracking-wider text-slate-500 font-bold block leading-tight">
                DOB
              </span>
              <span className="font-bold text-[8px] text-slate-900 block leading-snug truncate">
                {dob}
              </span>
            </div>
            <div className="min-w-0 border-t border-slate-200/80 pt-0.5">
              <span className="text-[6.5px] uppercase tracking-wider text-slate-500 font-bold block leading-tight">
                GENDER
              </span>
              <span className="font-bold text-[8px] text-slate-900 block leading-snug truncate">
                {gender}
              </span>
            </div>

            {/* Athlete sport & category row */}
            {isAthlete && (
              <div className="col-span-2 border-t border-slate-200/80 pt-0.5 flex flex-wrap items-center gap-1">
                <span className="bg-blue-50 border border-blue-200 rounded px-1.5 py-0.5 text-[7.5px] font-bold text-blue-900 leading-none truncate">
                  {athleteSport}
                </span>
                <span className="bg-purple-50 border border-purple-200 rounded px-1.5 py-0.5 text-[7.5px] font-extrabold text-purple-900 leading-none truncate">
                  Cat: {athleteCategory}
                </span>
                {showTeamAndCoach && coachName !== "—" && (
                  <span className="text-[7px] text-slate-500 font-semibold truncate leading-tight">
                    Coach: <strong className="text-slate-800">{coachName}</strong>
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Venue Beside Camp Dates Strip */}
      <div className="bg-slate-100/95 border-y border-slate-300 px-2 py-0.5 flex items-center justify-between text-[7.5px] font-bold text-slate-800 min-h-[16px] h-[16px] box-border leading-snug shrink-0">
        <div className="flex items-center gap-1 shrink-0">
          <FaCalendarAlt className="text-slate-500 text-[7px]" />
          <span><strong className="text-sky-700">DATES:</strong> {campDates}</span>
        </div>
        <div className="flex items-center gap-1 min-w-0 text-right truncate pl-2" title={`${campInfo.venue} (${campInfo.district})`}>
          <FaMapMarkerAlt className="text-red-500 text-[7px] shrink-0" />
          <span className="truncate"><strong className="text-red-700">VENUE:</strong> {campInfo.venue} &bull; <strong>DIST:</strong> {campInfo.district}</span>
        </div>
      </div>

      {/* Signatures Row: Official on Left, Secretary on Right with Space to Sign Above */}
      <div className="px-3 pt-0.5 pb-1 bg-white flex justify-between items-end shrink-0 box-border">
        {/* Left: Official, Samagra Siksha Abhiyan */}
        <div className="text-center w-[140px] flex flex-col items-center">
          <div className="h-5 w-full flex items-end justify-center overflow-hidden mb-0.5">
            {activePresSig ? (
              <img
                src={activePresSig}
                alt="Official Signature"
                className="max-h-5 max-w-[85px] w-auto object-contain"
              />
            ) : null}
          </div>
          <div className="border-t border-slate-400 w-full pt-0.5 leading-tight">
            <span className="text-[7.5px] font-extrabold text-slate-900 uppercase block">Official</span>
            <span className="text-[6.5px] font-bold text-blue-900 uppercase block">Samagra Siksha Abhiyan</span>
          </div>
        </div>

        {/* Right: V Ramaswamy, Secretary, Para Sports Association of Andhra Pradesh */}
        <div className="text-center w-[175px] flex flex-col items-center">
          <div className="h-5 w-full flex items-end justify-center overflow-hidden mb-0.5">
            {activeSecSig ? (
              <img
                src={activeSecSig}
                alt="Secretary Signature"
                className="max-h-5 max-w-[85px] w-auto object-contain"
              />
            ) : null}
          </div>
          <div className="border-t border-slate-400 w-full pt-0.5 leading-tight">
            <span className="text-[7.5px] font-extrabold text-slate-900 block">V Ramaswamy</span>
            <span className="text-[6px] font-bold text-blue-900 uppercase block whitespace-nowrap">Secretary, Para Sports Association of Andhra Pradesh</span>
          </div>
        </div>
      </div>

      {/* Footer Blue Line: Just Para Sports Association of Andhra Pradesh */}
      <div className="bg-[#1e3a8a] text-amber-300 px-2 flex items-center justify-center shrink-0 min-h-[16px] h-[16px] box-border leading-[16px] border-t border-white/20">
        <span className="text-[7.5px] font-extrabold tracking-wider uppercase text-amber-300">
          PARA SPORTS ASSOCIATION OF ANDHRA PRADESH
        </span>
      </div>
    </div>
  );
};

/**
 * Single ID Card View (Standard Portrait Event Badge)
 */
export const SingleCampIdCard = ({
  data,
  type = "athlete",
  camp = null,
  secretarySignature = null,
  signatures = null,
  onEditIdNumber = null,
  showTeamAndCoach = false,
}) => {
  const isAthlete = type === "athlete";
  const isCoach = type === "coach";
  const isOfficial = type === "official";

  const campDates = formatCampDates(camp);
  const campInfo = resolveCampVenueAndDistrict(camp, data);

  // Data mapping
  const name = data?.name || "Participant Name";
  const fatherName =
    data?.father_name ||
    data?.relation ||
    (isOfficial || isCoach ? "" : "Guardian");
  const district = resolveParticipantDistrict(data);
  const mobile = resolveParticipantMobile(data);
  const teamName = resolveParticipantTeam(data);
  const coachName = resolveParticipantCoach(data);
  const photoUrl = getCardPhoto(
    data?.photo ||
    data?.player?.photo ||
    data?.player_photo ||
    data?.profile_photo ||
    data?.user?.photo
  );

  const sigs = resolveCardSignatures(signatures || secretarySignature);
  const activePresSig = signatures?.president || sigs.president;
  const activeSecSig = signatures?.secretary || sigs.secretary;

  let roleTitle = "CAMP PARTICIPANT";
  let roleColor = "from-blue-700 via-indigo-800 to-blue-950";
  let roleBorder = "border-blue-600";
  let roleBadgeBg = "bg-blue-600";
  let roleTag = "ATHLETE";
  let RoleIcon = FaRunning;
  let identifierLabel = "TEAM";
  let identifierValue = teamName || "—";
  let primarySubDetail = "";
  let athleteSport = "Para Sports";
  let athleteCategory = "—";

  if (isAthlete) {
    roleTitle = "CAMP ATHLETE";
    roleColor = "from-blue-800 via-indigo-900 to-slate-900";
    roleBorder = "border-indigo-600";
    roleBadgeBg = "bg-indigo-600";
    roleTag = "ATHLETE";
    RoleIcon = FaRunning;
    identifierLabel = "TEAM";
    identifierValue = teamName || "—";

    // Discipline resolution:
    // If sport is unselected or empty -> "Para Sports"
    // If sport contains Athletics -> "Athletics"
    // Otherwise -> selected sport name
    const rawDisc =
      data?.discipline_name ||
      data?.discipline?.name ||
      data?.player?.discipline_name ||
      "";
    if (!rawDisc || !rawDisc.trim()) {
      athleteSport = "Para Sports";
    } else if (/athletics/i.test(rawDisc)) {
      athleteSport = "Athletics";
    } else {
      athleteSport = rawDisc.trim();
    }
    athleteCategory = resolveParticipantCategory(data);
    primarySubDetail = [athleteSport, athleteCategory !== "—" ? `Category: ${athleteCategory}` : "Category: —"].filter(Boolean).join(" • ");
  } else if (isCoach) {
    roleTitle = "CAMP ASSISTANT COACH";
    roleColor = "from-amber-700 via-orange-800 to-amber-950";
    roleBorder = "border-amber-500";
    roleBadgeBg = "bg-amber-600";
    roleTag = "ASSISTANT COACH";
    RoleIcon = FaUserShield;
    identifierLabel = "ASST COACH ID";
    identifierValue = `CCH-${String(data?.id || 1).padStart(3, "0")}`;
    primarySubDetail = data?.team_name ? `Team: ${data.team_name}` : "Camp Assistant Coach";
  } else if (isOfficial) {
    roleTitle = data?.designation || "CAMP OFFICIAL";
    roleColor = "from-emerald-800 via-teal-900 to-slate-950";
    roleBorder = "border-emerald-500";
    roleBadgeBg = "bg-emerald-600";
    roleTag = data?.duty_role || "OFFICIAL";
    RoleIcon = FaUserTie;
    identifierLabel = "OFFICIAL ID";
    identifierValue = data?.official_id_card_no || `OFF-${String(data?.id || 1).padStart(3, "0")}`;
    primarySubDetail = data?.duty_role || data?.designation || "Technical Official";
  }

  // Determine dynamic font scaling based on name length
  const nameLength = (name || "").length;
  let nameFontSize = "13px";
  if (nameLength > 30) {
    nameFontSize = "10.5px";
  } else if (nameLength > 20) {
    nameFontSize = "11.5px";
  }

  // ID Number: STRICTLY manually entered by user. No synthetic fallbacks.
  const idNumberDisplay =
    data?.id_number && String(data.id_number).trim() !== ""
      ? String(data.id_number).trim()
      : "—";

  return (
    <div
      className="camp-id-card-wrapper bg-white shadow-xl rounded-2xl overflow-hidden border border-slate-300 relative flex flex-col justify-between"
      style={{
        width: "100%",
        maxWidth: "345px",
        height: "100%",
        fontFamily: "'Inter', sans-serif",
      }}
    >
      {/* Top Gold / Tricolor Accent Bar */}
      <div className="h-1 w-full bg-gradient-to-r from-amber-400 via-emerald-500 to-blue-600 shrink-0" />

      {/* Card Header (Noticeably Enlarged Official Size) */}
      <div className="bg-slate-900 text-white px-3 py-2 flex items-center justify-between border-b border-amber-400/40 relative shrink-0 h-[50px] box-border">
        <img
          src="/images/parasports.png"
          alt="AP Para Sports"
          className="h-9 w-9 object-contain drop-shadow bg-white/10 rounded-full p-0.5 shrink-0"
          onError={(e) => {
            e.target.style.display = "none";
          }}
        />
        <div className="text-center flex-1 px-1.5 min-w-0">
          <p className="text-[10.5px] font-black uppercase tracking-tight text-amber-300 leading-none truncate">
            SAMAGRA SHIKSHA • 60 DAYS COACHING CAMP
          </p>
          <p className="text-[9px] font-bold text-slate-100 uppercase tracking-tight mt-1 leading-none truncate">
            Para Sports Association of Andhra Pradesh
          </p>
          <p className="text-[7.5px] text-slate-300 font-medium leading-none mt-1 truncate">
            Affiliated to Paralympic Committee of India (PCI) &amp; SAAP
          </p>
        </div>
        <img
          src="/images/ssa.png"
          alt="Samagra Shiksha"
          className="h-9 w-9 object-contain drop-shadow bg-white/10 rounded-full p-0.5 shrink-0"
          onError={(e) => {
            e.target.style.display = "none";
          }}
        />
      </div>

      {/* Role Banner Ribbon */}
      <div
        className={`bg-gradient-to-r ${roleColor} text-white px-2.5 py-0.5 flex items-center justify-between shadow-inner shrink-0`}
      >
        <div className="flex items-center gap-1.5">
          <RoleIcon className="text-amber-300 text-[10px]" />
          <span className="text-[9.5px] font-black uppercase tracking-wider text-amber-200">
            {roleTitle}
          </span>
        </div>
        <span className="text-[7.5px] font-extrabold px-2 py-0.2 rounded-full bg-white/20 text-white tracking-wider border border-white/30 uppercase">
          RESIDENTIAL COACHING CAMP
        </span>
      </div>

      {/* Card Body */}
      <div className="px-3 pt-2 pb-1.5 flex-1 flex flex-col justify-between bg-gradient-to-b from-slate-50/70 via-white to-slate-50/70 text-slate-800 min-h-0">
        {/* Top Segment: Photo + Name + Sport */}
        <div>
          <div className="flex gap-2.5 items-start">
            {/* Photo Frame */}
            <div className="flex flex-col items-center shrink-0">
              <div
                style={{ width: "74px", height: "90px" }}
                className={`rounded-xl overflow-hidden border-2 ${roleBorder} shadow-xs bg-slate-100 flex items-center justify-center p-0.5`}
              >
                <img
                  src={photoUrl}
                  alt={name}
                  className="w-full h-full object-cover rounded-lg"
                  onError={(e) => {
                    if (e.target.src !== DEFAULT_AVATAR && !e.target.src.endsWith(DEFAULT_AVATAR)) {
                      e.target.src = DEFAULT_AVATAR;
                    }
                  }}
                />
              </div>
              <span
                className={`mt-1 text-[7.5px] font-extrabold text-white px-2 py-0.5 rounded-full shadow-xs uppercase tracking-wider ${roleBadgeBg}`}
              >
                {roleTag}
              </span>
            </div>

            {/* Name & Primary Meta */}
            <div className="flex-1 min-w-0 pt-0.5">
              <h3
                className="font-black text-slate-900 leading-tight uppercase break-words"
                style={{ fontSize: nameFontSize, lineHeight: "1.15" }}
                title={name}
              >
                {name}
              </h3>
              {fatherName ? (
                <p className="text-[9.5px] text-slate-600 font-semibold break-words mt-0.5 leading-tight">
                  S/o, D/o: <span className="text-slate-800">{fatherName}</span>
                </p>
              ) : isOfficial ? (
                <p className="text-[9.5px] text-slate-600 font-semibold break-words mt-0.5 leading-tight">
                  Duty: <span className="text-emerald-900 font-bold">{data?.duty_role || "Official"}</span>
                </p>
              ) : isCoach ? (
                <p className="text-[9.5px] text-slate-600 font-semibold break-words mt-0.5 leading-tight">
                  Staff: <span className="text-amber-900 font-bold">{data?.designation || "Assistant Coach"}</span>
                </p>
              ) : null}
              {isAthlete ? (
                <div className="mt-1 flex flex-wrap items-center gap-1">
                  <span className="inline-block bg-blue-50 border border-blue-200/80 rounded px-2 py-0.5 text-[8.5px] font-bold text-blue-900 break-words leading-none">
                    {athleteSport}
                  </span>
                  <span
                    onClick={() => onEditIdNumber && onEditIdNumber(data)}
                    className={`inline-block border rounded px-2 py-0.5 text-[8.5px] font-bold break-words transition-colors leading-none ${athleteCategory && athleteCategory !== "—"
                      ? "bg-purple-50 border-purple-300 text-purple-900 font-extrabold"
                      : "bg-slate-100 border-slate-200 text-slate-400 italic"
                      } ${onEditIdNumber ? "cursor-pointer hover:border-purple-400" : ""}`}
                    title={onEditIdNumber ? "Click to set / edit Category & ID Number" : undefined}
                  >
                    Category: {athleteCategory}
                  </span>
                </div>
              ) : primarySubDetail ? (
                <div className="mt-1 inline-block bg-blue-50 border border-blue-200/80 rounded px-2 py-0.5 text-[9px] font-bold text-blue-900 break-words leading-none">
                  {primarySubDetail}
                </div>
              ) : null}
            </div>
          </div>

          {/* Balanced Metadata Grid */}
          <div className="mt-1.5 bg-slate-50/95 rounded-xl p-2 border border-slate-200 grid grid-cols-2 gap-x-2.5 gap-y-1">
            {/* ID Number / Official ID / Coach ID */}
            <div
              className={`flex items-start gap-1 min-w-0 ${onEditIdNumber && isAthlete
                ? "cursor-pointer hover:bg-slate-200/60 rounded p-0.5 transition-colors"
                : ""
                }`}
              onClick={() => onEditIdNumber && isAthlete && onEditIdNumber(data)}
              title={onEditIdNumber && isAthlete ? "Click to manually edit ID Number" : undefined}
            >
              <FaIdCard className={`${isOfficial ? "text-emerald-600" : isCoach ? "text-amber-600" : "text-indigo-600"} text-[10px] mt-0.5 shrink-0`} />
              <div className="min-w-0">
                <span className="text-[7px] uppercase tracking-wider text-slate-400 font-bold block leading-none mb-0.5">
                  {isOfficial ? "OFFICIAL ID" : isCoach ? "COACH ID" : "ID NUMBER"}
                </span>
                <span
                  className={`font-black font-mono text-[10px] block leading-tight select-all ${isOfficial
                    ? "text-emerald-950"
                    : isCoach
                      ? "text-amber-950"
                      : idNumberDisplay === "—"
                        ? "text-slate-400 italic"
                        : "text-indigo-950"
                    }`}
                >
                  {isOfficial
                    ? (data?.official_id_card_no || `OFF-${String(data?.id || 1).padStart(3, "0")}`)
                    : isCoach
                      ? (data?.coach_id || `CCH-${String(data?.id || 1).padStart(3, "0")}`)
                      : idNumberDisplay}
                </span>
              </div>
            </div>

            {/* Team Details (formerly BIB NO) */}
            <div className="flex items-start gap-1 min-w-0">
              <FaUsers className={`${isOfficial ? "text-teal-600" : isCoach ? "text-amber-600" : "text-amber-600"} text-[10px] mt-0.5 shrink-0`} />
              <div className="min-w-0">
                <span className="text-[7px] uppercase tracking-wider text-slate-400 font-bold block leading-none mb-0.5">
                  {isOfficial ? (data?.room_no ? "ROOM NO" : "DUTY ROLE") : isCoach ? "ASSIGNMENT" : identifierLabel}
                </span>
                <span className="text-slate-900 font-black text-[10px] block leading-tight select-all break-words">
                  {isOfficial ? (data?.room_no ? `Room ${data.room_no}` : (data?.duty_role || "Official")) : isCoach ? (data?.team_name || "Coaching Staff") : identifierValue}
                </span>
              </div>
            </div>

            {/* District */}
            <div className="flex items-start gap-1 min-w-0 border-t border-slate-200/80 pt-1">
              <FaMapMarkerAlt className="text-red-500 text-[10px] mt-0.5 shrink-0" />
              <div className="min-w-0">
                <span className="text-[7px] uppercase tracking-wider text-slate-400 font-bold block leading-none mb-0.5">
                  DISTRICT
                </span>
                <span
                  className="text-slate-900 font-bold text-[9.5px] block leading-tight break-words"
                  title={district}
                >
                  {district}
                </span>
              </div>
            </div>

            {/* Contact No */}
            <div className="flex items-start gap-1 min-w-0 border-t border-slate-200/80 pt-1">
              <FaPhoneAlt className="text-emerald-600 text-[9px] mt-0.5 shrink-0" />
              <div className="min-w-0">
                <span className="text-[7px] uppercase tracking-wider text-slate-400 font-bold block leading-none mb-0.5">
                  CONTACT NO
                </span>
                <span className="text-slate-900 font-bold font-mono text-[9.5px] block leading-tight select-all">
                  {mobile}
                </span>
              </div>
            </div>

            {/* Optional Assistant Coach Row */}
            {showTeamAndCoach && coachName !== "—" && (
              <div className="flex items-start gap-1 min-w-0 border-t border-slate-200/80 pt-1 col-span-2">
                <FaUserTie className="text-indigo-600 text-[10px] mt-0.5 shrink-0" />
                <div className="min-w-0">
                  <span className="text-[7px] uppercase tracking-wider text-slate-400 font-bold block leading-none mb-0.5">
                    ASSISTANT COACH
                  </span>
                  <span
                    className="font-bold text-[9px] text-slate-900 block leading-tight break-words"
                    title={coachName}
                  >
                    {coachName}
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Bottom Segment: Camp Duration + Signatory */}
        <div>
          {/* Camp Dates, Venue & District Strip */}
          <div className="my-1.5 bg-slate-100/90 rounded-md px-2 py-1 border border-slate-300 flex items-center justify-between text-[8px] text-slate-800 leading-none">
            <div className="flex items-center gap-1 shrink-0">
              <FaCalendarAlt className="text-slate-500 text-[8.5px]" />
              <span><strong>Dates:</strong> {campDates}</span>
            </div>
            <div className="flex items-center gap-1 min-w-0 text-right truncate pl-2" title={`${campInfo.venue} (${campInfo.district})`}>
              <FaMapMarkerAlt className="text-red-500 text-[8.5px] shrink-0" />
              <span className="truncate"><strong>Venue:</strong> {campInfo.venue} &bull; <strong>Dist:</strong> {campInfo.district}</span>
            </div>
          </div>

          {/* Dual Signatures Section: President on Left, Secretary on Right */}
          <div className="pt-1.5 border-t border-slate-200 flex items-center justify-between">
            {/* President / Official */}
            <div className="text-center flex flex-col items-center w-28">
              <div className="h-7 w-full flex items-end justify-center mb-0.5 overflow-hidden">
                {activePresSig ? (
                  <img
                    src={activePresSig}
                    alt="President Signature"
                    className="max-h-7 max-w-full w-auto h-auto object-contain object-bottom"
                  />
                ) : (
                  <span className="text-blue-900 font-serif italic text-xs font-bold tracking-wider opacity-90 select-none">
                  </span>
                )}
              </div>
              <div className="w-full border-t border-slate-400 pt-0.5 leading-none">
                <p className="text-[8px] font-black text-slate-900 leading-none">
                  OFFICIAL
                </p>
                <p className="text-[6.5px] font-extrabold text-blue-900 uppercase tracking-wider leading-none mt-0.5">
                  SAMAGRA SHIKSHA ABHIYAN
                </p>
              </div>
            </div>

            {/* Center Info */}
            <div className="text-center leading-none px-1">
              <p className="text-[7.5px] font-black text-slate-800 uppercase tracking-wider">
                OFFICIAL BADGE
              </p>
              <p className="text-[6.5px] text-slate-500 font-semibold mt-0.5">
                AP Para Sports Assn
              </p>
            </div>

            {/* Secretary */}
            <div className="text-center flex flex-col items-center w-28">
              <div className="h-7 w-full flex items-end justify-center mb-0.5 overflow-hidden">
                {activeSecSig ? (
                  <img
                    src={activeSecSig}
                    alt="Secretary Signature"
                    className="max-h-7 max-w-full w-auto h-auto object-contain object-bottom"
                  />
                ) : null}
              </div>
              <div className="w-full border-t border-slate-400 pt-0.5 leading-none">
                <p className="text-[8px] font-black text-slate-900 leading-none">
                  V Ramaswamy
                </p>
                <p className="text-[6.5px] font-extrabold text-blue-900 uppercase tracking-wider leading-none mt-0.5">
                  SECRETARY, PSAAP
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Footer Band */}
      <div className="bg-slate-900 text-amber-300 px-2.5 py-0.5 text-center text-[7px] font-bold tracking-wider uppercase flex justify-between items-center border-t border-amber-400/30 shrink-0">
        <span>SECURITY PASS • RESIDENTIAL CAMP</span>
        <span>VALID FOR DURATION ONLY</span>
      </div>
    </div>
  );
};

/**
 * Single ID Card View (Full Page A4 Format / Accreditation Poster)
 */
export const SingleA4Card = ({
  data,
  type = "athlete",
  camp = null,
  secretarySignature = null,
  signatures = null,
  onEditIdNumber = null,
  showTeamAndCoach = false,
}) => {
  const isAthlete = type === "athlete";
  const isCoach = type === "coach";
  const isOfficial = type === "official";

  const campDates = formatCampDates(camp);
  const campInfo = resolveCampVenueAndDistrict(camp, data);

  // Data mapping
  const name = data?.name || "Participant Name";
  const fatherName =
    data?.father_name ||
    data?.relation ||
    (isOfficial || isCoach ? "" : "Guardian");
  const district = resolveParticipantDistrict(data);
  const mobile = resolveParticipantMobile(data);
  const teamName = resolveParticipantTeam(data);
  const coachName = resolveParticipantCoach(data);
  const photoUrl = getCardPhoto(
    data?.photo ||
    data?.player?.photo ||
    data?.player_photo ||
    data?.profile_photo ||
    data?.user?.photo
  );

  const sigs = resolveCardSignatures(signatures || secretarySignature);
  const activeSecSig = signatures?.secretary || sigs.secretary;

  let roleTitle = "CAMP ATHLETE";
  let roleColor = "from-blue-800 via-indigo-900 to-slate-900";
  let roleBorder = "border-indigo-600";
  let roleBadgeBg = "bg-indigo-600";
  let roleTag = "ATHLETE";
  let RoleIcon = FaRunning;
  let idLabel = "ID NUMBER";
  let idNumberDisplay =
    data?.id_number && String(data.id_number).trim() !== ""
      ? String(data.id_number).trim()
      : "—";
  let secondaryLabel = "TEAM / SQUAD";
  let secondaryValue = teamName || "—";
  let athleteSport = "Para Sports";
  let athleteCategory = "—";

  if (isAthlete) {
    roleTitle = "CAMP ATHLETE";
    roleColor = "from-blue-800 via-indigo-900 to-slate-900";
    roleBorder = "border-indigo-600";
    roleBadgeBg = "bg-indigo-600";
    roleTag = "ATHLETE";
    RoleIcon = FaRunning;
    idLabel = "ID NUMBER";
    secondaryLabel = "TEAM / SQUAD";
    secondaryValue = teamName || "—";

    const rawDisc =
      data?.discipline_name ||
      data?.discipline?.name ||
      data?.player?.discipline_name ||
      "";
    if (!rawDisc || !rawDisc.trim()) {
      athleteSport = "Para Sports";
    } else if (/athletics/i.test(rawDisc)) {
      athleteSport = "Athletics";
    } else {
      athleteSport = rawDisc.trim();
    }
    athleteCategory = resolveParticipantCategory(data);
  } else if (isCoach) {
    roleTitle = "CAMP ASSISTANT COACH";
    roleColor = "from-amber-700 via-orange-800 to-amber-950";
    roleBorder = "border-amber-500";
    roleBadgeBg = "bg-amber-600";
    roleTag = "ASSISTANT COACH";
    RoleIcon = FaUserShield;
    idLabel = "COACH ID";
    idNumberDisplay = data?.coach_id || `CCH-${String(data?.id || 1).padStart(3, "0")}`;
    secondaryLabel = "ASSIGNMENT";
    secondaryValue = data?.team_name || "Coaching Staff";
  } else if (isOfficial) {
    roleTitle = data?.designation || "CAMP OFFICIAL";
    roleColor = "from-emerald-800 via-teal-900 to-slate-950";
    roleBorder = "border-emerald-500";
    roleBadgeBg = "bg-emerald-600";
    roleTag = data?.duty_role || "OFFICIAL";
    RoleIcon = FaUserTie;
    idLabel = "OFFICIAL ID";
    idNumberDisplay = data?.official_id_card_no || `OFF-${String(data?.id || 1).padStart(3, "0")}`;
    secondaryLabel = data?.room_no ? "ROOM NO" : "DUTY ROLE";
    secondaryValue = data?.room_no ? `Room ${data.room_no}` : (data?.duty_role || "Official");
  }

  return (
    <div
      className="a4-id-card-wrapper bg-white shadow-2xl rounded-2xl overflow-hidden border-2 border-slate-800 relative flex flex-col justify-between select-text"
      style={{
        width: "100%",
        maxWidth: "420px",
        height: "590px",
        maxHeight: "590px",
        boxSizing: "border-box",
        fontFamily: "'Inter', sans-serif",
      }}
    >
      {/* Top Accent Bar */}
      <div className="h-1.5 w-full bg-gradient-to-r from-amber-400 via-emerald-500 to-blue-600 shrink-0" />

      {/* Card Header (Noticeably Enlarged Official Size) */}
      <div className="bg-slate-900 text-white px-4 py-2 flex items-center justify-between border-b-2 border-amber-400/50 shrink-0 h-[56px] box-border">
        <img
          src="/images/parasports.png"
          alt="AP Para Sports"
          className="h-11 w-11 object-contain bg-white/10 rounded-full p-1 shrink-0"
          onError={(e) => { e.target.style.display = "none"; }}
        />
        <div className="text-center flex-1 px-2 min-w-0">
          <p className="text-[12px] font-black uppercase tracking-tight text-amber-300 leading-tight truncate">
            SAMAGRA SHIKSHA • 60 DAYS COACHING CAMP
          </p>
          <p className="text-[10px] font-bold text-slate-100 uppercase tracking-tight mt-0.5 leading-tight truncate">
            Para Sports Association of Andhra Pradesh
          </p>
          <p className="text-[8px] text-slate-300 font-medium leading-none mt-0.5 truncate">
            Affiliated to Paralympic Committee of India (PCI) &amp; SAAP
          </p>
        </div>
        <img
          src="/images/ssa.png"
          alt="Samagra Shiksha"
          className="h-11 w-11 object-contain bg-white/10 rounded-full p-1 shrink-0"
          onError={(e) => { e.target.style.display = "none"; }}
        />
      </div>

      {/* Role Banner Ribbon */}
      <div className={`bg-gradient-to-r ${roleColor} text-white px-4 py-1.5 flex items-center justify-between shadow-inner shrink-0`}>
        <div className="flex items-center gap-2 min-w-0">
          <RoleIcon className="text-amber-300 text-xs shrink-0" />
          <span className="text-[11px] font-black uppercase tracking-wider text-amber-200 truncate">
            {roleTitle}
          </span>
        </div>
        <span className="text-[8px] font-extrabold px-2.5 py-0.5 rounded-full bg-white/20 text-white tracking-wider border border-white/30 uppercase shrink-0">
          A4 ACCREDITATION CARD
        </span>
      </div>

      {/* Card Body */}
      <div className="px-4 py-3 flex-1 flex flex-col justify-between bg-gradient-to-b from-slate-50/80 via-white to-slate-50/80 text-slate-800 min-h-0 overflow-hidden box-border">
        <div>
          {/* Top Row: Photo + Name */}
          <div className="flex gap-3.5 items-start">
            <div className="flex flex-col items-center shrink-0 w-[92px]">
              <div
                style={{ width: "90px", height: "110px" }}
                className={`rounded-xl overflow-hidden border-2 ${roleBorder} shadow-sm bg-slate-100 flex items-center justify-center p-0.5`}
              >
                <img
                  src={photoUrl}
                  alt={name}
                  className="w-full h-full object-cover rounded-lg"
                  onError={(e) => {
                    if (e.target.src !== DEFAULT_AVATAR && !e.target.src.endsWith(DEFAULT_AVATAR)) {
                      e.target.src = DEFAULT_AVATAR;
                    }
                  }}
                />
              </div>
              <span className={`mt-1.5 text-[8.5px] font-extrabold text-white px-2.5 py-0.5 rounded-full shadow-xs uppercase tracking-wider ${roleBadgeBg} max-w-[90px] truncate`}>
                {roleTag}
              </span>
            </div>

            <div className="flex-1 min-w-0 pt-1">
              <h3
                className="font-black text-slate-900 leading-tight uppercase break-words text-sm md:text-base"
                title={name}
              >
                {name}
              </h3>
              {fatherName ? (
                <p className="text-[10px] text-slate-600 font-semibold truncate leading-tight mt-1">
                  S/o, D/o: <span className="text-slate-800 font-bold">{fatherName}</span>
                </p>
              ) : isOfficial ? (
                <p className="text-[10px] text-slate-600 font-semibold truncate leading-tight mt-1">
                  Duty: <span className="text-emerald-900 font-bold">{data?.duty_role || "Official"}</span>
                </p>
              ) : isCoach ? (
                <p className="text-[10px] text-slate-600 font-semibold truncate leading-tight mt-1">
                  Staff: <span className="text-amber-900 font-bold">{data?.designation || "Assistant Coach"}</span>
                </p>
              ) : null}

              {/* Chips */}
              <div className="mt-2 flex flex-wrap gap-1.5 items-center">
                <span className="bg-blue-50 border border-blue-200 rounded-md px-2 py-0.5 text-[9px] font-bold text-blue-900">
                  {athleteSport}
                </span>
                <span className="bg-purple-50 border border-purple-200 rounded-md px-2 py-0.5 text-[9px] font-extrabold text-purple-900">
                  Category: {athleteCategory}
                </span>
              </div>
              {showTeamAndCoach && coachName !== "—" && (
                <div className="mt-2">
                  <span className="inline-block bg-slate-100 border border-slate-200 rounded-md px-2 py-0.5 text-[8.5px] font-bold text-slate-700">
                    Coach: <strong className="text-slate-900">{coachName}</strong>
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Grid Metadata */}
          <div className="mt-3 bg-white rounded-xl p-2.5 border border-slate-200 grid grid-cols-2 gap-x-3 gap-y-2 shadow-xs">
            <div
              className={`min-w-0 ${onEditIdNumber && isAthlete ? "cursor-pointer hover:bg-slate-200/60 rounded px-1 transition-colors" : ""}`}
              onClick={() => onEditIdNumber && isAthlete && onEditIdNumber(data)}
            >
              <span className="text-[7.5px] uppercase tracking-wider text-slate-400 font-bold block leading-none">
                {idLabel}
              </span>
              <span className="font-black font-mono text-[12px] text-indigo-950 block leading-tight truncate">
                {idNumberDisplay}
              </span>
            </div>

            <div className="min-w-0">
              <span className="text-[7.5px] uppercase tracking-wider text-slate-400 font-bold block leading-none">
                {secondaryLabel}
              </span>
              <span className="font-bold text-[11px] text-amber-900 block leading-tight truncate" title={secondaryValue}>
                {secondaryValue}
              </span>
            </div>

            <div className="min-w-0 border-t border-slate-200 pt-1.5">
              <span className="text-[7.5px] uppercase tracking-wider text-slate-400 font-bold block leading-none">
                HOME DISTRICT
              </span>
              <span className="font-bold text-[10px] text-slate-900 block leading-tight truncate" title={district}>
                {district}
              </span>
            </div>

            <div className="min-w-0 border-t border-slate-200 pt-1.5">
              <span className="text-[7.5px] uppercase tracking-wider text-slate-400 font-bold block leading-none">
                CONTACT NO
              </span>
              <span className="font-bold font-mono text-[10px] text-slate-900 block leading-tight truncate">
                {mobile}
              </span>
            </div>
          </div>
        </div>

        {/* Camp Venue & Dates Box */}
        <div className="my-2 bg-slate-100/90 rounded-lg px-3 py-1.5 border border-slate-300 flex items-center justify-between text-[9px] text-slate-800 leading-tight">
          <div className="flex items-center gap-1.5 shrink-0">
            <FaCalendarAlt className="text-slate-500 text-[10px]" />
            <span><strong>Dates:</strong> {campDates}</span>
          </div>
          <div className="flex items-center gap-1.5 min-w-0 text-right truncate pl-2" title={`${campInfo.venue} (${campInfo.district})`}>
            <FaMapMarkerAlt className="text-red-500 text-[10px] shrink-0" />
            <span className="truncate"><strong>Venue:</strong> {campInfo.venue} &bull; <strong>Dist:</strong> {campInfo.district}</span>
          </div>
        </div>

        {/* Signature block above the blue association footer */}
        <div className="pt-2 border-t border-slate-200 flex items-center justify-between">
          <div className="text-left flex flex-col items-start w-1/3"><p className="text-[9px] font-black text-slate-900 leading-none">Official Signature</p><div className="w-28 border-t border-slate-400 mt-2" /></div>
          <div className="text-center w-1/3"><p className="text-[9px] font-black text-slate-800 uppercase tracking-wider">Official</p><p className="text-[8px] text-slate-500 font-semibold mt-0.5">Samagra Shiksha Abhiyan</p></div>
          <div className="text-right flex flex-col items-end w-1/3">
            <div className="h-8 w-full flex items-end justify-center mb-0.5 overflow-hidden">
              {activeSecSig ? (
                <img
                  src={activeSecSig}
                  alt="Secretary Signature"
                  className="max-h-7 max-w-[95px] w-auto object-contain"
                />
              ) : null}
            </div>
            <div className="w-36 border-t border-slate-400 pt-0.5 leading-none">
              <p className="text-[9px] font-black text-slate-900 leading-none">
                V Ramaswamy
              </p>
              <p className="text-[7.5px] font-extrabold text-blue-900 uppercase tracking-wider leading-none mt-0.5">
                Secretary, Para Sports Association of Andhra Pradesh
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Footer Band */}
      <div className="bg-slate-900 text-amber-300 px-4 py-1 text-center text-[8px] font-bold tracking-wider uppercase flex justify-center items-center border-t border-amber-400/30 shrink-0">
        <span>Para Sports Association of Andhra Pradesh</span>
      </div>
    </div>
  );
};

/**
 * ID Cards Management and Print Gallery
 */
const CampIdCard = ({
  camp,
  athletes = [],
  coaches = [],
  officials = [],
  selectedRoleFilter = "all",
  onUpdateAthlete = null,
  onNavigateTab = null,
  onEditOfficial = null,
}) => {
  const [roleFilter, setRoleFilter] = useState(selectedRoleFilter);
  const [searchTerm, setSearchTerm] = useState("");
  const [districtFilter, setDistrictFilter] = useState("");
  const [previewCard, setPreviewCard] = useState(null);
  const [signatureModalOpen, setSignatureModalOpen] = useState(false);
  const [secretarySignature, setSecretarySignature] = useState(() => {
    return typeof window !== "undefined"
      ? localStorage.getItem("camp_secretary_signature") || ""
      : "";
  });
  const [presidentSignature, setPresidentSignature] = useState(() => {
    return typeof window !== "undefined"
      ? localStorage.getItem("camp_president_signature") || ""
      : "";
  });

  const officialSignatures = {
    president: presidentSignature,
    secretary: secretarySignature,
  };

  // Automatically optimize and trim whitespace on stored signatures on mount
  useEffect(() => {
    if (secretarySignature && secretarySignature.startsWith("data:image")) {
      autoCropSignature(secretarySignature).then((cropped) => {
        if (cropped && cropped !== secretarySignature) {
          setSecretarySignature(cropped);
          try {
            localStorage.setItem("camp_secretary_signature", cropped);
          } catch (_e) {}
        }
      });
    }
    if (presidentSignature && presidentSignature.startsWith("data:image")) {
      autoCropSignature(presidentSignature).then((cropped) => {
        if (cropped && cropped !== presidentSignature) {
          setPresidentSignature(cropped);
          try {
            localStorage.setItem("camp_president_signature", cropped);
          } catch (_e) {}
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // State to toggle display of Team and Coach on all ID cards (persisted in localStorage)
  const [showTeamAndCoach, setShowTeamAndCoach] = useState(() => {
    try {
      return typeof window !== "undefined"
        ? localStorage.getItem("camp_id_show_team_coach") === "true"
        : false;
    } catch {
      return false;
    }
  });

  const handleToggleShowTeamAndCoach = () => {
    setShowTeamAndCoach((prev) => {
      const next = !prev;
      try {
        if (typeof window !== "undefined") {
          localStorage.setItem("camp_id_show_team_coach", String(next));
        }
      } catch (err) {
        console.warn("Could not save camp_id_show_team_coach to localStorage:", err);
      }
      return next;
    });
  };

  // Local state for athletes to allow immediate UI update when editing ID Number
  const [localAthletes, setLocalAthletes] = useState(athletes || []);
  useEffect(() => {
    setLocalAthletes(athletes || []);
  }, [athletes]);

  // Preview format: "badge" (portrait) or "pan" (landscape CR80)
  const [previewFormat, setPreviewFormat] = useState("badge");

  // Modal state for manual ID Number, Team & Category editing
  const [manualIdModal, setManualIdModal] = useState({
    isOpen: false,
    card: null,
    idNumber: "",
    category: "",
    teamName: "",
    saving: false,
  });

  const signatureInputRef = useRef(null);
  const presidentSignatureInputRef = useRef(null);

  // Handle secretary signature file upload
  const handleSignatureFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      Swal.fire("Invalid File", "Please upload a valid image file (PNG, JPG, SVG, WebP).", "warning");
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      Swal.fire("File Too Large", "Please upload an image smaller than 2MB.", "warning");
      return;
    }

    const reader = new FileReader();
    reader.onload = async (event) => {
      const rawDataUrl = event.target.result;
      const cropped = await autoCropSignature(rawDataUrl);
      setSecretarySignature(cropped);
      try {
        localStorage.setItem("camp_secretary_signature", cropped);
      } catch (err) {
        console.warn("Could not save secretary signature to localStorage:", err);
      }
      Swal.fire({
        icon: "success",
        title: "Secretary Signature Auto-Fitted",
        text: "Signature cropped and auto-fitted to perfectly fill the card signature area!",
        timer: 2000,
        showConfirmButton: false,
      });
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveSignature = () => {
    setSecretarySignature("");
    try {
      localStorage.removeItem("camp_secretary_signature");
    } catch (err) {
      console.warn("Could not clear secretary signature from localStorage:", err);
    }
    if (signatureInputRef.current) {
      signatureInputRef.current.value = "";
    }
    Swal.fire({
      icon: "info",
      title: "Secretary Signature Removed",
      text: "The signature space is now blank until a new image is uploaded.",
      timer: 1800,
      showConfirmButton: false,
    });
  };

  // Handle president signature file upload
  const handlePresidentSignatureFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      Swal.fire("Invalid File", "Please upload a valid image file (PNG, JPG, SVG, WebP).", "warning");
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      Swal.fire("File Too Large", "Please upload an image smaller than 2MB.", "warning");
      return;
    }

    const reader = new FileReader();
    reader.onload = async (event) => {
      const rawDataUrl = event.target.result;
      const cropped = await autoCropSignature(rawDataUrl);
      setPresidentSignature(cropped);
      try {
        localStorage.setItem("camp_president_signature", cropped);
      } catch (err) {
        console.warn("Could not save Official signature to localStorage:", err);
      }
      Swal.fire({
        icon: "success",
        title: "Official Signature Auto-Fitted",
        text: "Signature cropped and auto-fitted to perfectly fill the card signature area!",
        timer: 2000,
        showConfirmButton: false,
      });
    };
    reader.readAsDataURL(file);
  };

  const handleRemovePresidentSignature = () => {
    setPresidentSignature("");
    try {
      localStorage.removeItem("camp_president_signature");
    } catch (err) {
      console.warn("Could not clear Official signature from localStorage:", err);
    }
    if (presidentSignatureInputRef.current) {
      presidentSignatureInputRef.current.value = "";
    }
    Swal.fire({
      icon: "info",
      title: "Official Signature Removed",
      text: "Reverted to official default font signature for Official.",
      timer: 1800,
      showConfirmButton: false,
    });
  };

  // Open manual ID, Team & Category edit modal
  const handleOpenManualIdModal = (card) => {
    const currentTeam = resolveParticipantTeam(card);
    setManualIdModal({
      isOpen: true,
      card,
      idNumber: card?.id_number || "",
      category: card?.category || card?.player?.category || "",
      teamName: card?.team_name || (currentTeam === "—" ? "" : currentTeam),
      saving: false,
    });
  };

  // Save manual ID Number, Team & Category via API
  const handleSaveManualId = async (e) => {
    e?.preventDefault?.();
    if (!manualIdModal.card) return;

    const athleteId = manualIdModal.card.id;
    const newIdNumber = manualIdModal.idNumber.trim();
    const newCategory = manualIdModal.category.trim();
    const newTeamName = manualIdModal.teamName.trim();

    setManualIdModal((prev) => ({ ...prev, saving: true }));
    try {
      const payload = {
        id_number: newIdNumber,
        category: newCategory,
        team_name: newTeamName,
      };
      const res = await (API.camp?.updateIdNumber
        ? API.camp.updateIdNumber(athleteId, payload)
        : API.post(`/admin/camp/athletes/${athleteId}/id-number`, payload));

      if (res?.success) {
        // Update local list
        setLocalAthletes((prev) =>
          prev.map((a) =>
            a.id === athleteId
              ? { ...a, id_number: newIdNumber, category: newCategory, team_name: newTeamName }
              : a
          )
        );

        if (previewCard && previewCard.id === athleteId) {
          setPreviewCard((prev) => ({
            ...prev,
            id_number: newIdNumber,
            category: newCategory,
            team_name: newTeamName,
          }));
        }

        if (onUpdateAthlete) {
          onUpdateAthlete({
            ...manualIdModal.card,
            id_number: newIdNumber,
            category: newCategory,
            team_name: newTeamName,
          });
        }

        setManualIdModal({ isOpen: false, card: null, idNumber: "", category: "", teamName: "", saving: false });

        Swal.fire({
          icon: "success",
          title: "Athlete Details Saved",
          text: `ID: "${newIdNumber || "—"}" • Team: "${newTeamName || "—"}" • Cat: "${newCategory || "—"}" saved successfully!`,
          timer: 1800,
          showConfirmButton: false,
        });
      } else {
        throw new Error(res?.message || "Failed to save details");
      }
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: err?.message || "Failed to update details.",
      });
      setManualIdModal((prev) => ({ ...prev, saving: false }));
    }
  };

  // Normalize athletes list using localAthletes (excluding removed_from_camp)
  const athleteCards = (localAthletes || [])
    .filter((a) => a.status !== "removed_from_camp")
    .map((a) => ({
      ...a,
      _cardType: "athlete",
    }));

  // Normalize coaches list
  const coachCards = (coaches || []).map((c) => ({
    ...c,
    _cardType: "coach",
  }));

  // Normalize officials list
  const officialCards = (officials || []).map((o) => ({
    ...o,
    _cardType: "official",
  }));

  // Combined and filtered list
  const filteredCards = [...athleteCards, ...coachCards, ...officialCards].filter((item) => {
    // Role filter
    if (roleFilter !== "all" && item._cardType !== roleFilter) return false;

    // District filter
    const itemDistrict = resolveParticipantDistrict(item);
    if (districtFilter && itemDistrict !== districtFilter) return false;

    // Search term filter
    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase().trim();
      const matchName = item.name?.toLowerCase().includes(q);
      const matchDist = itemDistrict.toLowerCase().includes(q);
      const matchTeam = resolveParticipantTeam(item).toLowerCase().includes(q);
      const matchId = (item.id_number || item.coach_id || item.official_id_card_no || "")
        .toLowerCase()
        .includes(q);
      const matchMobile = resolveParticipantMobile(item).includes(q);
      if (!matchName && !matchDist && !matchTeam && !matchId && !matchMobile) return false;
    }

    return true;
  });

  const districtsList = Array.from(
    new Set(
      [...athleteCards, ...coachCards, ...officialCards]
        .map((i) => resolveParticipantDistrict(i))
        .filter((d) => Boolean(d) && d !== "-")
    )
  ).sort();

  // --- Portrait Badge Handlers ---
  const handleDownloadSinglePdf = (card) => {
    if (!card) return;
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    const singleHtml = generateSingleCardSheetHtml(card, camp, officialSignatures, showTeamAndCoach, origin);
    const cleanName = (card.name || "Participant").replace(/\s+/g, "_");
    downloadHtmlAsPdf(singleHtml, `Camp_ID_Card_${cleanName}`, { orientation: "portrait" });
  };

  const handlePrintSingleCard = (card) => {
    if (!card) return;
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    const singleHtml = generateSingleCardSheetHtml(card, camp, officialSignatures, showTeamAndCoach, origin);
    printHtmlViaIframe(singleHtml);
  };

  const handleDownloadSinglePerPagePdf = async () => {
    if (filteredCards.length === 0) return;
    Swal.fire({
      title: "Preparing ID Badges...",
      html: `<div style="font-size: 13px; color: #64748b;">Compiling ${filteredCards.length} single-per-page badges for server export...</div>`,
      allowOutsideClick: false,
      showConfirmButton: false,
      didOpen: () => Swal.showLoading(),
    });
    await new Promise((r) => setTimeout(r, 60));
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    const allHtml = generateSinglePerPageSheetsHtml(filteredCards, camp, officialSignatures, showTeamAndCoach, origin);
    downloadHtmlAsPdf(allHtml, `Camp_ID_Cards_Single_Per_Page_${roleFilter}`, { orientation: "portrait" });
  };

  const handlePrintSinglePerPage = () => {
    if (filteredCards.length === 0) return;
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    const allHtml = generateSinglePerPageSheetsHtml(filteredCards, camp, officialSignatures, showTeamAndCoach, origin);
    printHtmlViaIframe(allHtml);
  };

  // --- PAN Card Size (CR80) Handlers ---
  const handleDownloadSinglePanPdf = (card) => {
    if (!card) return;
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    const panHtml = generateSinglePanCardSheetHtml(card, camp, officialSignatures, showTeamAndCoach, origin);
    const cleanName = (card.name || "Participant").replace(/\s+/g, "_");
    downloadHtmlAsPdf(panHtml, `PAN_Camp_ID_${cleanName}`, { orientation: "portrait" });
  };

  const handlePrintSinglePanCard = (card) => {
    if (!card) return;
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    const panHtml = generateSinglePanCardSheetHtml(card, camp, officialSignatures, showTeamAndCoach, origin);
    printHtmlViaIframe(panHtml);
  };

  const handleDownloadPanBatchPdf = async () => {
    if (filteredCards.length === 0) return;
    Swal.fire({
      title: "Preparing PAN ID Sheets...",
      html: `<div style="font-size: 13px; color: #64748b;">Compiling ${filteredCards.length} cards (8 per A4 sheet) for server export...</div>`,
      allowOutsideClick: false,
      showConfirmButton: false,
      didOpen: () => Swal.showLoading(),
    });
    await new Promise((r) => setTimeout(r, 60));
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    const allHtml = generatePanCardBatchSheetsHtml(filteredCards, camp, officialSignatures, showTeamAndCoach, origin);
    downloadHtmlAsPdf(allHtml, `Camp_PAN_Cards_8PerPage_${roleFilter}`, { orientation: "portrait" });
  };

  const handlePrintPanBatch = () => {
    if (filteredCards.length === 0) return;
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    const allHtml = generatePanCardBatchSheetsHtml(filteredCards, camp, officialSignatures, showTeamAndCoach, origin);
    printHtmlViaIframe(allHtml);
  };

  const handleDownloadPanSinglePerPagePdf = async () => {
    if (filteredCards.length === 0) return;
    Swal.fire({
      title: "Preparing Single PAN Cards...",
      html: `<div style="font-size: 13px; color: #64748b;">Compiling ${filteredCards.length} cards for server export...</div>`,
      allowOutsideClick: false,
      showConfirmButton: false,
      didOpen: () => Swal.showLoading(),
    });
    await new Promise((r) => setTimeout(r, 60));
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    const allHtml = generatePanSinglePerPageSheetsHtml(filteredCards, camp, officialSignatures, showTeamAndCoach, origin);
    downloadHtmlAsPdf(allHtml, `Camp_PAN_Cards_SinglePerPage_${roleFilter}`, { orientation: "portrait" });
  };

  const handlePrintPanSinglePerPage = () => {
    if (filteredCards.length === 0) return;
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    const allHtml = generatePanSinglePerPageSheetsHtml(filteredCards, camp, officialSignatures, showTeamAndCoach, origin);
    printHtmlViaIframe(allHtml);
  };

  // --- A4 Full Page Size Handlers ---
  const handleDownloadSingleA4Pdf = (card) => {
    if (!card) return;
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    const a4Html = generateSingleA4CardSheetHtml(card, camp, officialSignatures, showTeamAndCoach, origin);
    const cleanName = (card.name || "Participant").replace(/\s+/g, "_");
    downloadHtmlAsPdf(a4Html, `Camp_A4_ID_Card_${cleanName}`, { orientation: "portrait" });
  };

  const handlePrintSingleA4Card = (card) => {
    if (!card) return;
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    const a4Html = generateSingleA4CardSheetHtml(card, camp, officialSignatures, showTeamAndCoach, origin);
    printHtmlViaIframe(a4Html);
  };

  const handleDownloadA4AllPdf = async () => {
    if (filteredCards.length === 0) return;
    Swal.fire({
      title: "Preparing A4 ID Cards...",
      html: `<div style="font-size: 13px; color: #64748b;">Compiling ${filteredCards.length} full A4 cards for server export...</div>`,
      allowOutsideClick: false,
      showConfirmButton: false,
      didOpen: () => Swal.showLoading(),
    });
    await new Promise((r) => setTimeout(r, 60));
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    const allHtml = generateA4BatchSheetsHtml(filteredCards, camp, officialSignatures, showTeamAndCoach, origin);
    downloadHtmlAsPdf(allHtml, `Camp_A4_ID_Cards_Batch_${roleFilter}`, { orientation: "portrait" });
  };

  const handlePrintA4All = () => {
    if (filteredCards.length === 0) return;
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    const allHtml = generateA4BatchSheetsHtml(filteredCards, camp, officialSignatures, showTeamAndCoach, origin);
    printHtmlViaIframe(allHtml);
  };

  return (
    <div className="space-y-6">
      {/* Header Bar */}
      <div className="bg-white p-5 rounded-2xl shadow-sm border border-slate-200 flex flex-col md:flex-row md:items-center justify-between gap-4 print:hidden">
        <div>
          <h2 className="text-xl font-black text-slate-900 flex items-center gap-2">
            <FaIdCard className="text-blue-600" />
            Camp Identity Cards & Badges
          </h2>
          <p className="text-xs text-slate-500 mt-1">
            Generate and download official laminated identity cards for Athletes, Coaches, and Camp Officials in Badge, 100×70mm, and A4 formats.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          {/* Team & Coaches Toggle Button */}
          <button
            type="button"
            onClick={handleToggleShowTeamAndCoach}
            className={`flex items-center gap-2 px-3.5 py-2.5 rounded-xl text-xs font-bold shadow-xs hover:shadow transition-all cursor-pointer border ${showTeamAndCoach
              ? "bg-indigo-600 hover:bg-indigo-700 text-white border-indigo-700 shadow-sm"
              : "bg-slate-100 hover:bg-slate-200 text-slate-700 border-slate-300"
              }`}
            title="Toggle showing Team and Coach information on participant ID cards"
          >
            <FaUsers className={showTeamAndCoach ? "text-amber-300 text-sm" : "text-slate-500 text-sm"} />
            <span>Team & Coaches</span>
            <span
              className={`px-1.5 py-0.5 rounded text-[9px] font-black uppercase tracking-wider ${showTeamAndCoach
                ? "bg-white/25 text-white"
                : "bg-slate-200 text-slate-700"
                }`}
            >
              {showTeamAndCoach ? "SHOW" : "HIDE"}
            </span>
          </button>

          {/* Official Signatures (President & Secretary) Upload Button */}
          <button
            onClick={() => setSignatureModalOpen(true)}
            className="flex items-center gap-2 px-3.5 py-2.5 bg-slate-800 hover:bg-slate-900 text-white rounded-xl text-xs font-bold shadow-sm hover:shadow transition-all cursor-pointer"
            title="Upload official scanned signatures for Official and Secretary (V Ramaswamy)"
          >
            <FaSignature className="text-amber-400 text-sm" />
            <span>Official Signatures (2)</span>
            {presidentSignature && secretarySignature ? (
              <span className="px-1.5 py-0.5 bg-emerald-500/20 border border-emerald-400/40 text-emerald-300 text-[9px] font-extrabold rounded-full flex items-center gap-1">
                <FaCheckCircle className="text-[8px]" /> 2 Active
              </span>
            ) : presidentSignature || secretarySignature ? (
              <span className="px-1.5 py-0.5 bg-blue-500/20 border border-blue-400/40 text-blue-300 text-[9px] font-extrabold rounded-full flex items-center gap-1">
                <FaCheckCircle className="text-[8px]" /> 1 Active
              </span>
            ) : (
              <span className="px-1.5 py-0.5 bg-amber-500/20 border border-amber-400/40 text-amber-300 text-[9px] font-extrabold rounded-full">
                Default Font
              </span>
            )}
          </button>

          {/* NEW: Print A4 Size Button */}
          <button
            type="button"
            onClick={handlePrintA4All}
            disabled={filteredCards.length === 0}
            className="flex items-center gap-2 px-3.5 py-2.5 bg-rose-700 hover:bg-rose-800 text-white rounded-xl text-xs font-bold shadow-md hover:shadow-lg transition-all disabled:opacity-50 cursor-pointer"
            title="Set and print all ID cards enlarged to full A4 page size"
          >
            <FaPrint className="text-amber-300" /> Print A4 Size
          </button>

          {/* NEW: Download A4 Size PDF Button */}
          <button
            type="button"
            onClick={handleDownloadA4AllPdf}
            disabled={filteredCards.length === 0}
            className="flex items-center gap-2 px-3.5 py-2.5 bg-rose-900 hover:bg-black text-white rounded-xl text-xs font-bold shadow-md hover:shadow-lg transition-all disabled:opacity-50 cursor-pointer"
            title="Download all ID cards in full A4 page size (PDF)"
          >
            <FaDownload /> A4 Size (PDF)
          </button>

          {/* 1 Card / Page Direct Print */}
          <button
            type="button"
            onClick={handlePrintSinglePerPage}
            disabled={filteredCards.length === 0}
            className="flex items-center gap-2 px-3.5 py-2.5 bg-indigo-800 hover:bg-indigo-900 text-white rounded-xl text-xs font-bold shadow-md hover:shadow-lg transition-all disabled:opacity-50 cursor-pointer"
            title="Direct print 1 ID card per A4 sheet"
          >
            <FaPrint /> Print (1 Card/Page)
          </button>

          {/* 1 Card / Page PDF Option */}
          <button
            type="button"
            onClick={handleDownloadSinglePerPagePdf}
            disabled={filteredCards.length === 0}
            className="flex items-center gap-2 px-3.5 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-xs font-bold shadow-md hover:shadow-lg transition-all disabled:opacity-50 cursor-pointer"
            title="Download all badges with 1 ID card centered per A4 page in PDF"
          >
            <FaDownload /> 1 Card / Page (PDF)
          </button>

          {/* PAN Card Size (100x70mm 8 / Sheet) PDF Download */}
          <button
            type="button"
            onClick={handleDownloadPanBatchPdf}
            disabled={filteredCards.length === 0}
            className="flex items-center gap-2 px-3.5 py-2.5 bg-amber-500 hover:bg-amber-600 text-slate-950 font-black rounded-xl text-xs shadow-md hover:shadow-lg transition-all disabled:opacity-50 cursor-pointer"
            title="Download ID cards in 100mm x 70mm size (8 cards per A4 page in PDF)"
          >
            <FaDownload /> 100×70mm (8/Sheet PDF)
          </button>

          {/* Print 100x70mm PAN Cards (8 per Sheet) */}
          <button
            type="button"
            onClick={handlePrintPanBatch}
            disabled={filteredCards.length === 0}
            className="flex items-center gap-2 px-3.5 py-2.5 bg-slate-900 hover:bg-black text-amber-300 border border-amber-400/40 rounded-xl text-xs font-bold shadow-md hover:shadow-lg transition-all disabled:opacity-50 cursor-pointer"
            title="Direct print 100mm x 70mm size sheets (8 cards per A4 page)"
          >
            <FaPrint /> Print 8/Sheet (100×70mm)
          </button>

          {/* 1 PAN Card / Page PDF */}
          <button
            type="button"
            onClick={handleDownloadPanSinglePerPagePdf}
            disabled={filteredCards.length === 0}
            className="flex items-center gap-2 px-3.5 py-2.5 bg-teal-700 hover:bg-teal-800 text-white rounded-xl text-xs font-bold shadow-md hover:shadow-lg transition-all disabled:opacity-50 cursor-pointer"
            title="Download 1 ID card (100x70mm) centered per A4 page in PDF"
          >
            <FaDownload /> 1 Card (100×70mm PDF)
          </button>

          {/* Print 1 PAN Card / Page (Direct Individual Print) */}
          <button
            type="button"
            onClick={handlePrintPanSinglePerPage}
            disabled={filteredCards.length === 0}
            className="flex items-center gap-2 px-3.5 py-2.5 bg-indigo-700 hover:bg-indigo-800 text-white rounded-xl text-xs font-bold shadow-md hover:shadow-lg transition-all disabled:opacity-50 cursor-pointer"
            title="Direct print 1 ID card (100mm x 70mm) centered per A4 page"
          >
            <FaPrint /> Print 1 Card (100×70mm)
          </button>

          {onNavigateTab && (
            <button
              type="button"
              onClick={() => onNavigateTab("officials")}
              className="flex items-center gap-2 px-3.5 py-2.5 bg-emerald-700 hover:bg-emerald-800 text-white rounded-xl text-xs font-bold shadow-sm hover:shadow transition-all cursor-pointer"
              title="Add or enroll camp technical officials, referees, and medical staff"
            >
              <FaUserTie className="text-emerald-300 text-sm" />
              <span>Officials Staff</span>
            </button>
          )}
        </div>
      </div>

      {/* Filter and Count Summary Bar */}
      <div className="bg-white p-4 rounded-2xl shadow-sm border border-slate-200 flex flex-wrap items-center justify-between gap-4 print:hidden">
        {/* Role Tabs */}
        <div className="flex flex-wrap items-center gap-2">
          {[
            { key: "all", label: "All Participants", count: athleteCards.length + coachCards.length + officialCards.length, icon: FaUsers },
            { key: "athlete", label: "Athletes", count: athleteCards.length, icon: FaRunning },
            { key: "coach", label: "Assistant Coaches", count: coachCards.length, icon: FaUserShield },
            { key: "official", label: "Camp Officials", count: officialCards.length, icon: FaUserTie },
          ].map((tab) => {
            const Icon = tab.icon;
            const active = roleFilter === tab.key;
            return (
              <button
                key={tab.key}
                type="button"
                onClick={() => setRoleFilter(tab.key)}
                className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold transition-all cursor-pointer ${active
                  ? "bg-slate-900 text-white shadow-sm"
                  : "bg-slate-100 hover:bg-slate-200 text-slate-700"
                  }`}
              >
                <Icon className={active ? "text-amber-400" : "text-slate-500"} />
                <span>{tab.label}</span>
                <span
                  className={`px-1.5 py-0.2 text-[10px] rounded-full font-black ${active ? "bg-white/20 text-white" : "bg-slate-200 text-slate-700"
                    }`}
                >
                  {tab.count}
                </span>
              </button>
            );
          })}
        </div>

        {/* Filters: District & Search */}
        <div className="flex flex-wrap items-center gap-3">
          {/* District Dropdown */}
          <div className="relative">
            <select
              value={districtFilter}
              onChange={(e) => setDistrictFilter(e.target.value)}
              className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer pr-8"
            >
              <option value="">All Districts ({districtsList.length})</option>
              {districtsList.map((dist) => (
                <option key={dist} value={dist}>
                  {dist}
                </option>
              ))}
            </select>
          </div>

          {/* Search Box */}
          <div className="relative">
            <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-xs" />
            <input
              type="text"
              placeholder="Search by name, ID, Team, District..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-8 pr-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 w-56"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm("")}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 text-xs"
              >
                ×
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Screen Gallery of ID Cards */}
      <div className="print:hidden">
        {filteredCards.length === 0 ? (
          <div className="bg-white rounded-2xl p-12 text-center border border-slate-200 shadow-xs">
            <FaIdCard className="mx-auto text-4xl text-slate-300 mb-3" />
            <h3 className="text-base font-bold text-slate-800">No Participants Found</h3>
            <p className="text-xs text-slate-500 mt-1">
              {searchTerm || districtFilter
                ? "Try clearing your search or district filters to view available cards."
                : "No participants have been registered in this coaching camp yet."}
            </p>
            {(searchTerm || districtFilter) && (
              <div className="mt-4 flex justify-center gap-2">
                <button
                  onClick={() => {
                    setSearchTerm("");
                    setDistrictFilter("");
                  }}
                  className="px-3.5 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-xs font-bold transition-colors cursor-pointer"
                >
                  Clear Filters
                </button>
              </div>
            )}
            {roleFilter === "official" && onNavigateTab && (
              <div className="mt-4">
                <button
                  type="button"
                  onClick={() => onNavigateTab("officials")}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-700 hover:bg-emerald-800 text-white rounded-xl text-xs font-bold shadow transition cursor-pointer"
                >
                  <FaUserTie /> Go to Officials & Technical Staff Tab to Enroll
                </button>
              </div>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-6 justify-items-center">
            {filteredCards.map((card, idx) => (
              <div key={`${card._cardType}-${card.id || idx}`} className="flex flex-col items-center gap-2.5">
                <SingleCampIdCard
                  data={card}
                  type={card._cardType}
                  camp={camp}
                  signatures={officialSignatures}
                  secretarySignature={secretarySignature}
                  onEditIdNumber={card._cardType === "athlete" ? handleOpenManualIdModal : null}
                  showTeamAndCoach={showTeamAndCoach}
                />
                <div className="flex flex-wrap items-center justify-center gap-1.5">
                  {card._cardType === "athlete" && (
                    <button
                      onClick={() => handleOpenManualIdModal(card)}
                      className="flex items-center gap-1.5 px-2.5 py-1 bg-amber-50 hover:bg-amber-100 text-amber-800 border border-amber-300 rounded-lg text-[11px] font-bold cursor-pointer transition-colors shadow-xs"
                      title="Manually enter / edit ID Number & Team Details"
                    >
                      <FaEdit className="text-xs" /> Edit ID & Team
                    </button>
                  )}
                  {card._cardType === "official" && onEditOfficial && (
                    <button
                      onClick={() => onEditOfficial(card)}
                      className="flex items-center gap-1.5 px-2.5 py-1 bg-emerald-50 hover:bg-emerald-100 text-emerald-800 border border-emerald-300 rounded-lg text-[11px] font-bold cursor-pointer transition-colors shadow-xs"
                      title="Edit Official details, designation, duty role & ID card number"
                    >
                      <FaEdit className="text-xs" /> Edit Official
                    </button>
                  )}
                  <button
                    onClick={() => handlePrintSingleA4Card(card)}
                    className="flex items-center gap-1.5 px-2.5 py-1 bg-rose-700 hover:bg-rose-800 text-white rounded-lg text-[11px] font-bold shadow-xs cursor-pointer transition-colors"
                    title="Set and print this ID card to full A4 page size"
                  >
                    <FaPrint className="text-xs text-amber-300" /> Print A4
                  </button>
                  <button
                    onClick={() => handleDownloadSingleA4Pdf(card)}
                    className="flex items-center gap-1.5 px-2.5 py-1 bg-rose-900 hover:bg-black text-white rounded-lg text-[11px] font-bold shadow-xs cursor-pointer transition-colors"
                    title="Download this ID card in full A4 page size (PDF)"
                  >
                    <FaDownload className="text-xs text-amber-300" /> A4 PDF
                  </button>
                  <button
                    onClick={() => handlePrintSingleCard(card)}
                    className="flex items-center gap-1.5 px-2.5 py-1 bg-slate-700 hover:bg-slate-800 text-white rounded-lg text-[11px] font-bold shadow-xs cursor-pointer transition-colors"
                    title="Direct print this ID badge"
                  >
                    <FaPrint className="text-xs text-amber-300" /> Print
                  </button>
                  <button
                    onClick={() => handleDownloadSinglePdf(card)}
                    className="flex items-center gap-1.5 px-2.5 py-1 bg-slate-800 hover:bg-slate-900 text-white rounded-lg text-[11px] font-bold shadow-xs cursor-pointer transition-colors"
                    title="Download portrait ID badge in PDF"
                  >
                    <FaDownload className="text-xs text-amber-300" /> PDF
                  </button>
                  <button
                    onClick={() => handleDownloadSinglePanPdf(card)}
                    className="flex items-center gap-1.5 px-2.5 py-1 bg-amber-500 hover:bg-amber-600 text-slate-950 font-black rounded-lg text-[11px] shadow-xs cursor-pointer transition-colors"
                    title="Download this ID card in 100mm x 70mm size"
                  >
                    <FaDownload className="text-xs" /> 100×70mm PDF
                  </button>
                  <button
                    onClick={() => handlePrintSinglePanCard(card)}
                    className="flex items-center gap-1.5 px-2.5 py-1 bg-slate-900 hover:bg-black text-amber-300 border border-amber-400/40 rounded-lg text-[11px] font-bold shadow-xs cursor-pointer transition-colors"
                    title="Direct print 100mm x 70mm pass for this participant"
                  >
                    <FaPrint className="text-xs" /> Print 100×70mm
                  </button>
                  <button
                    onClick={() => setPreviewCard(card)}
                    className="flex items-center gap-1.5 px-2.5 py-1 bg-blue-50 hover:bg-blue-100 text-blue-700 border border-blue-200 rounded-lg text-[11px] font-bold cursor-pointer transition-colors"
                  >
                    <FaIdCard className="text-xs" /> Full View
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Full View Modal on Screen */}
      {previewCard && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-xs flex items-center justify-center p-4 print:hidden overflow-y-auto">
          <div className={`bg-white rounded-3xl p-6 shadow-2xl ${previewFormat === "a4" ? "max-w-xl" : "max-w-md"} w-full flex flex-col items-center gap-4 relative animate-in fade-in zoom-in-95 my-8`}>
            <button
              onClick={() => setPreviewCard(null)}
              className="absolute top-4 right-4 text-slate-400 hover:text-slate-700 bg-slate-100 hover:bg-slate-200 p-2 rounded-full cursor-pointer transition-colors"
            >
              <FaTimes />
            </button>

            <div className="flex flex-wrap items-center justify-between w-full pr-8 gap-2">
              <h3 className="text-sm font-extrabold text-slate-800 uppercase tracking-wider flex items-center gap-2">
                <FaIdCard className="text-blue-600" /> Card Preview
              </h3>

              <div className="flex items-center gap-2">
                {/* Format Toggle: Badge vs 100x70mm vs A4 Size */}
                <div className="flex items-center gap-1 bg-slate-100 p-1 rounded-xl border border-slate-200">
                  <button
                    type="button"
                    onClick={() => setPreviewFormat("badge")}
                    className={`px-2 py-1 rounded-lg text-xs font-bold transition-all cursor-pointer ${previewFormat === "badge"
                      ? "bg-white text-indigo-900 shadow-xs"
                      : "text-slate-600 hover:text-slate-900"
                      }`}
                  >
                    Badge
                  </button>
                  <button
                    type="button"
                    onClick={() => setPreviewFormat("pan")}
                    className={`px-2 py-1 rounded-lg text-xs font-black transition-all cursor-pointer ${previewFormat === "pan"
                      ? "bg-amber-500 text-slate-950 shadow-xs"
                      : "text-slate-600 hover:text-slate-900"
                      }`}
                  >
                    100×70mm
                  </button>
                  <button
                    type="button"
                    onClick={() => setPreviewFormat("a4")}
                    className={`px-2 py-1 rounded-lg text-xs font-black transition-all cursor-pointer ${previewFormat === "a4"
                      ? "bg-rose-600 text-white shadow-xs"
                      : "text-slate-600 hover:text-slate-900"
                      }`}
                  >
                    A4 Size
                  </button>
                </div>

                <label className="flex items-center gap-1.5 text-xs font-semibold text-slate-700 bg-slate-100 hover:bg-slate-200 px-2 py-1 rounded-lg cursor-pointer transition-colors select-none">
                  <input
                    type="checkbox"
                    checked={showTeamAndCoach}
                    onChange={handleToggleShowTeamAndCoach}
                    className="rounded text-indigo-600 focus:ring-indigo-500 h-3.5 w-3.5 cursor-pointer"
                  />
                  <span className="text-[11px]">Team & Coach</span>
                </label>
              </div>
            </div>

            <div className="camp-single-preview-wrapper w-full flex justify-center py-2">
              {previewFormat === "a4" ? (
                <SingleA4Card
                  data={previewCard}
                  type={previewCard._cardType}
                  camp={camp}
                  signatures={officialSignatures}
                  secretarySignature={secretarySignature}
                  onEditIdNumber={previewCard._cardType === "athlete" ? handleOpenManualIdModal : null}
                  showTeamAndCoach={showTeamAndCoach}
                />
              ) : previewFormat === "pan" ? (
                <SinglePanCard
                  data={previewCard}
                  type={previewCard._cardType}
                  camp={camp}
                  signatures={officialSignatures}
                  secretarySignature={secretarySignature}
                  onEditIdNumber={previewCard._cardType === "athlete" ? handleOpenManualIdModal : null}
                  showTeamAndCoach={showTeamAndCoach}
                />
              ) : (
                <SingleCampIdCard
                  data={previewCard}
                  type={previewCard._cardType}
                  camp={camp}
                  signatures={officialSignatures}
                  secretarySignature={secretarySignature}
                  onEditIdNumber={previewCard._cardType === "athlete" ? handleOpenManualIdModal : null}
                  showTeamAndCoach={showTeamAndCoach}
                />
              )}
            </div>

            <div className="flex flex-wrap items-center gap-2 w-full pt-2 border-t border-slate-100">
              {previewCard._cardType === "athlete" && (
                <button
                  onClick={() => handleOpenManualIdModal(previewCard)}
                  className="py-2.5 px-3 bg-amber-50 hover:bg-amber-100 text-amber-800 border border-amber-300 rounded-xl text-xs font-bold cursor-pointer transition-colors flex items-center gap-1.5"
                  title="Edit ID Number & Team Details"
                >
                  <FaEdit /> Edit ID & Team
                </button>
              )}
              {previewCard._cardType === "official" && onEditOfficial && (
                <button
                  onClick={() => {
                    const c = previewCard;
                    setPreviewCard(null);
                    onEditOfficial(c);
                  }}
                  className="py-2.5 px-3 bg-emerald-50 hover:bg-emerald-100 text-emerald-800 border border-emerald-300 rounded-xl text-xs font-bold cursor-pointer transition-colors flex items-center gap-1.5"
                >
                  <FaEdit /> Edit Official
                </button>
              )}
              <button
                onClick={() => setPreviewCard(null)}
                className="py-2.5 px-3 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-xs font-bold cursor-pointer transition-colors"
              >
                Close
              </button>
              <button
                onClick={() =>
                  previewFormat === "a4"
                    ? handlePrintSingleA4Card(previewCard)
                    : previewFormat === "pan"
                      ? handlePrintSinglePanCard(previewCard)
                      : handlePrintSingleCard(previewCard)
                }
                className="flex-1 py-2.5 bg-slate-800 hover:bg-slate-900 text-white rounded-xl text-xs font-bold shadow-md cursor-pointer flex items-center justify-center gap-1.5 transition-all"
                title="Direct print this ID card"
              >
                <FaPrint /> Print {previewFormat === "a4" ? "A4 Card" : previewFormat === "pan" ? "100×70mm" : "Badge"}
              </button>
              <button
                onClick={() =>
                  previewFormat === "a4"
                    ? handleDownloadSingleA4Pdf(previewCard)
                    : previewFormat === "pan"
                      ? handleDownloadSinglePanPdf(previewCard)
                      : handleDownloadSinglePdf(previewCard)
                }
                className={`flex-1 py-2.5 text-xs font-black rounded-xl shadow-md cursor-pointer flex items-center justify-center gap-1.5 transition-all ${previewFormat === "a4"
                  ? "bg-rose-600 hover:bg-rose-700 text-white"
                  : previewFormat === "pan"
                    ? "bg-amber-500 hover:bg-amber-600 text-slate-950"
                    : "bg-blue-600 hover:bg-blue-700 text-white"
                  }`}
              >
                <FaDownload /> Download {previewFormat === "a4" ? "A4 (PDF)" : previewFormat === "pan" ? "100×70mm (PDF)" : "Badge (PDF)"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ============================================================== */}
      {/* MODAL: MANUALLY ENTER / EDIT ID NUMBER */}
      {/* ============================================================== */}
      {manualIdModal.isOpen && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-4 print:hidden">
          <div className="bg-white rounded-3xl p-6 shadow-2xl max-w-sm w-full relative animate-in fade-in zoom-in-95 space-y-4">
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
              <div className="flex items-center gap-2">
                <div className="w-9 h-9 rounded-xl bg-indigo-50 border border-indigo-200 flex items-center justify-center text-indigo-700">
                  <FaIdCard className="text-base" />
                </div>
                <div>
                  <h3 className="text-sm font-extrabold text-slate-900">Edit ID, Team & Category</h3>
                  <p className="text-[11px] text-slate-500 truncate max-w-[200px]">
                    Athlete: <strong className="text-slate-800">{manualIdModal.card?.name}</strong>
                  </p>
                </div>
              </div>
              <button
                onClick={() => setManualIdModal({ isOpen: false, card: null, idNumber: "", category: "", teamName: "", saving: false })}
                className="text-slate-400 hover:text-slate-700 bg-slate-100 hover:bg-slate-200 p-2 rounded-full cursor-pointer transition-colors"
              >
                <FaTimes />
              </button>
            </div>

            <form onSubmit={handleSaveManualId} className="space-y-3">
              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">
                  ID Number (Printed on Card)
                </label>
                <input
                  type="text"
                  placeholder="e.g. CAMP-001 or AP-023"
                  value={manualIdModal.idNumber}
                  onChange={(e) => setManualIdModal((prev) => ({ ...prev, idNumber: e.target.value }))}
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded-xl text-slate-900 font-mono font-bold text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  autoFocus
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">
                  Team Name / Assigned Team
                </label>
                <input
                  type="text"
                  placeholder="e.g. Warriors, Titans, Team Blue, Group A"
                  value={manualIdModal.teamName}
                  onChange={(e) => setManualIdModal((prev) => ({ ...prev, teamName: e.target.value }))}
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded-xl text-slate-900 font-bold text-xs focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider">
                    Category / Classification
                  </label>
                  <span className="text-[10px] text-slate-400 font-medium">e.g. T54, F55, T11</span>
                </div>
                <input
                  type="text"
                  placeholder="e.g. T54, F55, T11, Locomotor"
                  value={manualIdModal.category}
                  onChange={(e) => setManualIdModal((prev) => ({ ...prev, category: e.target.value }))}
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded-xl text-slate-900 font-bold text-xs focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 uppercase"
                />
                {/* Quick suggestions */}
                <div className="mt-1.5 flex flex-wrap items-center gap-1">
                  <span className="text-[10px] text-slate-400 font-semibold mr-1">Quick:</span>
                  {["T54", "F55", "T11", "T12", "T13", "F46", "T44", "F57", "T38"].map((chip) => (
                    <button
                      key={chip}
                      type="button"
                      onClick={() => setManualIdModal((prev) => ({ ...prev, category: chip }))}
                      className="text-[9px] font-bold px-1.5 py-0.5 bg-slate-100 hover:bg-purple-100 hover:text-purple-800 text-slate-600 rounded border border-slate-200 cursor-pointer transition-colors"
                    >
                      {chip}
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex items-center gap-2 pt-2 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setManualIdModal({ isOpen: false, card: null, idNumber: "", category: "", teamName: "", saving: false })}
                  className="flex-1 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold rounded-xl text-xs cursor-pointer transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={manualIdModal.saving}
                  className="flex-1 py-2 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl text-xs shadow-sm cursor-pointer transition-all disabled:opacity-50"
                >
                  {manualIdModal.saving ? "Saving..." : "Save Details"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ============================================================== */}
      {/* MODAL: UPLOAD OFFICIAL SIGNATURES (PRESIDENT & SECRETARY) */}
      {/* ============================================================== */}
      {signatureModalOpen && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-4 print:hidden overflow-y-auto">
          <div className="bg-white rounded-3xl p-6 shadow-2xl max-w-2xl w-full relative animate-in fade-in zoom-in-95 space-y-5 my-8">
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
              <div className="flex items-center gap-2.5">
                <div className="w-10 h-10 rounded-2xl bg-amber-50 border border-amber-200 flex items-center justify-center text-amber-700">
                  <FaSignature className="text-lg" />
                </div>
                <div>
                  <h3 className="text-base font-extrabold text-slate-900">Official Signatures (ID Cards & Badges)</h3>
                  <p className="text-xs text-slate-500">
                    Both signatures are stamped on all identity cards: President (Left) &amp; Secretary (Right)
                  </p>
                </div>
              </div>
              <button
                onClick={() => setSignatureModalOpen(false)}
                className="text-slate-400 hover:text-slate-700 bg-slate-100 hover:bg-slate-200 p-2 rounded-full cursor-pointer transition-colors"
              >
                <FaTimes />
              </button>
            </div>

            {/* Hidden File Inputs */}
            <input
              type="file"
              ref={presidentSignatureInputRef}
              accept="image/*"
              onChange={handlePresidentSignatureFileChange}
              className="hidden"
            />
            <input
              type="file"
              ref={signatureInputRef}
              accept="image/*"
              onChange={handleSignatureFileChange}
              className="hidden"
            />

            {/* Dual Signatures Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* 1. PRESIDENT SIGNATURE */}
              <div className="bg-slate-50 rounded-2xl p-4 border border-slate-200 flex flex-col justify-between space-y-3">
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-[11px] font-black text-slate-800 uppercase tracking-wider flex items-center gap-1.5">
                      <span className="w-2 h-2 rounded-full bg-blue-600 inline-block"></span>
                      1. President / Director
                    </span>
                    {presidentSignature ? (
                      <span className="px-2 py-0.5 bg-emerald-100 text-emerald-800 border border-emerald-300 text-[10px] font-extrabold rounded-full flex items-center gap-1">
                        <FaCheckCircle className="text-[9px]" /> Custom Image
                      </span>
                    ) : (
                      <span className="px-2 py-0.5 bg-amber-100 text-amber-800 border border-amber-300 text-[10px] font-extrabold rounded-full">
                        Default Font
                      </span>
                    )}
                  </div>
                  <p className="text-[11px] text-slate-600 mb-2">
                    Signatory: <strong className="text-slate-900">Official</strong> (Samagra Shiksha Abhiyan)
                  </p>

                  {/* Stamp Box */}
                  <div className="h-16 bg-white border border-dashed border-slate-300 rounded-xl flex items-center justify-center p-2 shadow-inner">
                    {presidentSignature ? (
                      <img
                        src={presidentSignature}
                        alt="President Signature"
                        className="max-h-14 max-w-full object-contain"
                      />
                    ) : (
                      <div className="text-center">
                        <span className="text-blue-900 font-serif italic text-base font-bold tracking-wider select-none">

                        </span>
                        <span className="block text-[9px] text-slate-400 font-sans">
                          (Default stylized font signature)
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="space-y-2 pt-1 border-t border-slate-200/60">
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => presidentSignatureInputRef.current?.click()}
                      className="flex-1 py-2 px-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-xs font-bold shadow-xs transition-all flex items-center justify-center gap-1.5 cursor-pointer"
                    >
                      <FaUpload /> Upload Scan
                    </button>
                    {presidentSignature && (
                      <button
                        type="button"
                        onClick={handleRemovePresidentSignature}
                        className="py-2 px-2.5 bg-red-50 hover:bg-red-100 text-red-600 border border-red-200 rounded-xl text-xs font-bold transition-all flex items-center gap-1 cursor-pointer"
                        title="Remove custom image and reset to default font"
                      >
                        <FaTrash /> Remove
                      </button>
                    )}
                  </div>
                  <p className="text-[10px] text-slate-400 text-center">
                    PNG, JPG, SVG, WebP (Transparent/white recommended)
                  </p>
                </div>
              </div>

              {/* 2. GENERAL SECRETARY SIGNATURE */}
              <div className="bg-slate-50 rounded-2xl p-4 border border-slate-200 flex flex-col justify-between space-y-3">
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-[11px] font-black text-slate-800 uppercase tracking-wider flex items-center gap-1.5">
                      <span className="w-2 h-2 rounded-full bg-indigo-600 inline-block"></span>
                      2. General Secretary
                    </span>
                    {secretarySignature ? (
                      <span className="px-2 py-0.5 bg-emerald-100 text-emerald-800 border border-emerald-300 text-[10px] font-extrabold rounded-full flex items-center gap-1">
                        <FaCheckCircle className="text-[9px]" /> Custom Image
                      </span>
                    ) : (
                      <span className="px-2 py-0.5 bg-amber-100 text-amber-800 border border-amber-300 text-[10px] font-extrabold rounded-full">
                        Default Font
                      </span>
                    )}
                  </div>
                  <p className="text-[11px] text-slate-600 mb-2">
                    Signatory: <strong className="text-slate-900">V Ramaswamy</strong> (General Secretary)
                  </p>

                  {/* Stamp Box */}
                  <div className="h-16 bg-white border border-dashed border-slate-300 rounded-xl flex items-center justify-center p-2 shadow-inner">
                    {secretarySignature ? (
                      <img
                        src={secretarySignature}
                        alt="Secretary Signature"
                        className="max-h-14 max-w-full object-contain"
                      />
                    ) : (
                      <span className="text-[10px] text-slate-400 font-semibold">Upload signature image</span>
                    )}
                  </div>
                </div>

                <div className="space-y-2 pt-1 border-t border-slate-200/60">
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => signatureInputRef.current?.click()}
                      className="flex-1 py-2 px-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-xs font-bold shadow-xs transition-all flex items-center justify-center gap-1.5 cursor-pointer"
                    >
                      <FaUpload /> Upload Scan
                    </button>
                    {secretarySignature && (
                      <button
                        type="button"
                        onClick={handleRemoveSignature}
                        className="py-2 px-2.5 bg-red-50 hover:bg-red-100 text-red-600 border border-red-200 rounded-xl text-xs font-bold transition-all flex items-center gap-1 cursor-pointer"
                        title="Remove custom image and reset to default font"
                      >
                        <FaTrash /> Remove
                      </button>
                    )}
                  </div>
                  <p className="text-[10px] text-slate-400 text-center">
                    PNG, JPG, SVG, WebP (Transparent/white recommended)
                  </p>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="pt-2 border-t border-slate-100 flex items-center justify-between">
              <p className="text-[11px] text-slate-500">
                Signatures persist in browser storage and apply instantly to all PDF downloads and print sheets.
              </p>
              <button
                type="button"
                onClick={() => setSignatureModalOpen(false)}
                className="px-5 py-2.5 bg-slate-900 hover:bg-black text-white font-bold rounded-xl text-xs cursor-pointer transition-colors shadow-sm"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CampIdCard;
